import { BackWallGridTexture } from "./BackWallGridTexture";
import { FloorGridTexture } from "./FloorGridTexture";
import { SideWallGridTexture } from "./SideWallGridTexture";
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useFrame } from '../../utils/useFrameP1';
import { useP1RenderCommit } from '../../utils/p1Probe';
import { useRenderProbe } from './useRenderProbe'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { OrbitControls, Html, Text } from '@react-three/drei';
import FastLine from './FastLine';
import { useSideXClip } from './useSideXClip';
import { replay } from '../../utils/replayHooks';
import { pickWallBatch } from '../../utils/wallBatch';
import { histRuntime } from '../../utils/historyMode';
import FastSegments from './FastSegments';
import { ProgressiveContour } from './ProgressiveContour';
import CrosshairRenderer from './CrosshairRenderer';
import { DataPoint } from '../../utils/MarketDataGenerator';
import { computeSliceShift } from '../../utils/sliceShift';
import type { UploadedBatch } from '../../utils/uploadedBatch';
import { clampDragTarget, NAV_VISIBLE_CANDLES, maxNavOffset } from '../../utils/historyNav';
import { navRec, navSource, navDrag, SRC, LIM } from '../../utils/navRecorder';
import { touchRec, touchDrag } from '../../utils/touchRecorder';
import { strokeBegin, strokeStep, strokeEnd, strokeOwns, strokeActive, strokeMayBegin, NO_POINTER, type StrokeRefs } from '../../utils/dragStroke';
import { positionFor, targetFor, setTargetFor, motionOf } from '../../utils/anchoredNav';
import { VisibleScaleController, VisibleDepthController, visibleBatchRange, yAffine, yToPrice, priceToY } from '../../utils/visibleScale';
import { LiveScaleTicks, LiveScaleDomain } from './LiveScaleTicks';
import { CubeNames } from './CubeNames';
import { DBG_ENABLED, dbgScene, dbgScaleFrame, dbgScaleCalc, dbgEvent, dbgDragWrite } from '../../utils/debugTelemetry';
import { syncCount, SC } from '../../utils/syncProbe';
import { useFilamentSyncProbe } from './useSyncProbe';
import { useSideProbe } from './useSideProbe';
import { clipToX, newGpuCopy, type GpuCopy } from '../../utils/sideClip';
import InstancedCandles from '../InstancedCandles';
import { GroupedLineSegments } from './GroupedLineSegments';
import { cn } from '../../lib/utils';
import { Trash2, X, Lock, MoreHorizontal, Palette, Activity } from 'lucide-react';
import { PriceZone } from '../../types';
import { OverlayLogos3D } from './OverlayLogos3D';
import { useFlowaesStore } from '../../store/useFlowaesStore';
import { useWaveGeometry, useWaveColors, smaDamping } from '../../utils/waveGeometry';
import { DynamicTimeAxis, DynamicForceAxis } from './DynamicAxes';
import { CurrentValueTrackers } from './CurrentValueTrackers';
import { isWithin1DBounds } from '../../utils/boundsHelper';

import { getTimeframeSeparation, getPeriodLabel, getSeparatorIndices } from '../../utils/timeUtils';
import { HoldToUnlock } from '../ui/HoldToUnlock';

// F3a (memória): as funções memorizadas da cena são criadas por FÁBRICAS fora do componente. Uma closure criada dentro
// do componente fecha sobre o contexto inteiro do render (no V8 o contexto é compartilhado por todas as closures da
// função). Memos recriadas em renders diferentes prendiam os contextos uns dos outros numa corrente sem fim, cada elo
// com os dados daquele render: o heap crescia com a navegação (replay profundo: 24 → 114 MB). Aqui cada closure guarda
// só os parâmetros; funções vindas de outros hooks entram por ref (o valor atual), não pela closure.
const mkScaleFn = (k: number) => (v: number) => v * k;
const mkDepthFn = (wallZ: number, frontZ: number) => (z: number) => (z - wallZ) / (frontZ - wallZ);
const mkTimeXFn = (tMin: number, tMax: number, w: number, x0: number) => (val: number) => ((val - tMin) / (tMax - tMin)) * w + x0;
const mkCancelTimer = (timerRef: { current: any }) => () => {
  if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
};
const mkStartTimer = (timerRef: { current: any }, setModeRef: { current: any }, cancel: () => void) => () => {
  cancel();
  timerRef.current = setTimeout(() => {
    timerRef.current = null;
    const setMode = setModeRef.current;
    if (typeof setMode === 'function') setMode('orbit');
    useFlowaesStore.getState().setActiveGesture(null);
  }, 10000); // 10 seconds of inactivity before reverting to orbit mode
};
const mkForceZLive = (S: { current: any }, fallback: (v: number) => number) => (v: number) => {
  const st = S.current; return st.ready ? v * st.zScale : fallback(v);
};
const mkYLive = (S: { current: any }, fnRef: { current: (p: number) => number }, h: number) => (p: number) => {
  const st = S.current; return st.ready ? priceToY(p, st.dMin, st.dMax, h) : fnRef.current(p);
};
const mkPriceLive = (S: { current: any }, fnRef: { current: (y: number) => number }, h: number) => (y: number) => {
  const st = S.current; return st.ready ? yToPrice(y, st.dMin, st.dMax, h) : fnRef.current(y);
};
import { appProfiler } from '../../utils/appProfiler';




function useSmaSideBuffers(
  key: 'sma10' | 'sma20' | 'sma50',
  baseHex: string,
  data: any[],
  sideX: number,
  sideHistoryShadowStrength: number,
  getSmaHelixY: (d: any, key: string) => number,
  getSmaHelixZ: (d: any, key: string) => number
) {
  const ptsRef = useRef<Float32Array>(new Float32Array(3000));
  const colsRef = useRef<Float32Array>(new Float32Array(3000));

  return useMemo(() => {
    let validCount = 0;
    for (let i = 0; i < data.length; i++) {
      if (data[i][key] !== undefined) validCount++;
    }
    if (validCount < 2) return { pts: new Float32Array(0), colors: new Float32Array(0) };

    const minDistanceSq = 0.0001;
    let lastX = -99999, lastY = -99999, lastZ = -99999;
    const decimatedPts: {x:number, y:number, z:number}[] = [];

    for (let i = 0; i < data.length; i++) {
        const d = data[i];
        if (d[key] === undefined) continue;
        
        const isLast = i === data.length - 1;
        const py = getSmaHelixY(d, key);
        const pz = getSmaHelixZ(d, key);
        const dx = sideX - lastX;
        const dy = py - lastY;
        const dz = pz - lastZ;

        if (isLast || (dx*dx + dy*dy + dz*dz) > minDistanceSq) {
            decimatedPts.push({ x: sideX, y: py, z: pz });
            lastX = sideX; lastY = py; lastZ = pz;
        }
    }

    if (decimatedPts.length < 2) return { pts: new Float32Array(0), colors: new Float32Array(0) };

    const baseColor = new THREE.Color(baseHex);
    let finalPtsCount = 0;

    if (decimatedPts.length > 250) {
      finalPtsCount = decimatedPts.length;
      const requiredLength = finalPtsCount * 3;

      if (ptsRef.current.length < requiredLength) {
          let newCap = ptsRef.current.length * 2;
          while (newCap < requiredLength) newCap *= 2;
          ptsRef.current = new Float32Array(newCap);
          colsRef.current = new Float32Array(newCap);
      }

      const pBuf = ptsRef.current;
      const cBuf = colsRef.current;
      const len = decimatedPts.length;

      for (let i = 0; i < len; i++) {
          const p = decimatedPts[i];
          const idx = i * 3;
          pBuf[idx] = p.x;
          pBuf[idx+1] = p.y;
          pBuf[idx+2] = p.z;

          const u = i / (len - 1);
          const maxFade = 0.85;
          const minFade = maxFade * Math.max(0.2, 1 - sideHistoryShadowStrength);
          const historyFade = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;

          cBuf[idx] = baseColor.r * historyFade;
          cBuf[idx+1] = baseColor.g * historyFade;
          cBuf[idx+2] = baseColor.b * historyFade;
      }
    } else {
       const vecPts = decimatedPts.map(p => new THREE.Vector3(p.x, p.y, p.z));
       const curve = new THREE.CatmullRomCurve3(vecPts, false, 'catmullrom', 0.5);
       const totalSamples = Math.min(300, vecPts.length * 2);
       finalPtsCount = totalSamples + 1;
       const requiredLength = finalPtsCount * 3;

       if (ptsRef.current.length < requiredLength) {
          let newCap = ptsRef.current.length * 2;
          while (newCap < requiredLength) newCap *= 2;
          ptsRef.current = new Float32Array(newCap);
          colsRef.current = new Float32Array(newCap);
       }

       const pBuf = ptsRef.current;
       const cBuf = colsRef.current;
       const targetVec = new THREE.Vector3();

       for (let i = 0; i <= totalSamples; i++) {
          const u = i / totalSamples;
          curve.getPoint(u, targetVec);
          const idx = i * 3;
          pBuf[idx] = targetVec.x;
          pBuf[idx+1] = targetVec.y;
          pBuf[idx+2] = targetVec.z;

          const maxFade = 0.85;
          const minFade = maxFade * Math.max(0.2, 1 - sideHistoryShadowStrength);
          const historyFade = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;

          cBuf[idx] = baseColor.r * historyFade;
          cBuf[idx+1] = baseColor.g * historyFade;
          cBuf[idx+2] = baseColor.b * historyFade;
       }
    }

    return {
       pts: ptsRef.current.subarray(0, finalPtsCount * 3),
       colors: colsRef.current.subarray(0, finalPtsCount * 3)
    };
  }, [data, sideX, sideHistoryShadowStrength, key, baseHex, getSmaHelixY, getSmaHelixZ]);
}

function useSma3DBuffers(
  key: 'sma10' | 'sma20' | 'sma50',
  baseHex: string,
  data: any[],
  startX: number,
  backChartWidth: number,
  historyShadowStrength: number,
  getSmaHelixY: (d: any, key: string) => number,
  getSmaHelixZ: (d: any, key: string) => number
) {
  const ptsRef = useRef<Float32Array>(new Float32Array(3000));
  const colsRef = useRef<Float32Array>(new Float32Array(3000));

  return useMemo(() => {
    let validCount = 0;
    for (let i = 0; i < data.length; i++) {
      if (data[i][key] !== undefined) validCount++;
    }
    if (validCount < 2) return { pts: new Float32Array(0), colors: new Float32Array(0) };

    const minDistanceSq = 0.0001;
    let lastX = -99999, lastY = -99999, lastZ = -99999;
    const decimatedPts: {x:number, y:number, z:number}[] = [];

    for (let i = 0; i < data.length; i++) {
        const d = data[i];
        if (d[key] === undefined) continue;
        
        const isLast = i === data.length - 1;
        const px = startX + (i / Math.max(1, (data.length - 1))) * backChartWidth;
        const py = getSmaHelixY(d, key);
        const pz = getSmaHelixZ(d, key);
        const dx = px - lastX;
        const dy = py - lastY;
        const dz = pz - lastZ;

        if (isLast || (dx*dx + dy*dy + dz*dz) > minDistanceSq) {
            decimatedPts.push({ x: px, y: py, z: pz });
            lastX = px; lastY = py; lastZ = pz;
        }
    }

    if (decimatedPts.length < 2) return { pts: new Float32Array(0), colors: new Float32Array(0) };

    const baseColor = new THREE.Color(baseHex);
    let finalPtsCount = 0;

    if (decimatedPts.length > 250) {
      finalPtsCount = decimatedPts.length;
      const requiredLength = finalPtsCount * 3;

      if (ptsRef.current.length < requiredLength) {
          let newCap = ptsRef.current.length * 2;
          while (newCap < requiredLength) newCap *= 2;
          ptsRef.current = new Float32Array(newCap);
          colsRef.current = new Float32Array(newCap);
      }

      const pBuf = ptsRef.current;
      const cBuf = colsRef.current;
      const len = decimatedPts.length;

      for (let i = 0; i < len; i++) {
          const p = decimatedPts[i];
          const idx = i * 3;
          pBuf[idx] = p.x;
          pBuf[idx+1] = p.y;
          pBuf[idx+2] = p.z;

          const u = i / (len - 1);
          const maxFade = 0.85;
          const minFade = maxFade * Math.max(0.2, 1 - historyShadowStrength);
          const historyFade = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;

          cBuf[idx] = baseColor.r * historyFade;
          cBuf[idx+1] = baseColor.g * historyFade;
          cBuf[idx+2] = baseColor.b * historyFade;
      }
    } else {
       const vecPts = decimatedPts.map(p => new THREE.Vector3(p.x, p.y, p.z));
       const curve = new THREE.CatmullRomCurve3(vecPts, false, 'catmullrom', 0.5);
       const totalSamples = Math.min(300, vecPts.length * 2);
       finalPtsCount = totalSamples + 1;
       const requiredLength = finalPtsCount * 3;

       if (ptsRef.current.length < requiredLength) {
          let newCap = ptsRef.current.length * 2;
          while (newCap < requiredLength) newCap *= 2;
          ptsRef.current = new Float32Array(newCap);
          colsRef.current = new Float32Array(newCap);
       }

       const pBuf = ptsRef.current;
       const cBuf = colsRef.current;
       const targetVec = new THREE.Vector3();

       for (let i = 0; i <= totalSamples; i++) {
          const u = i / totalSamples;
          curve.getPoint(u, targetVec);
          const idx = i * 3;
          pBuf[idx] = targetVec.x;
          pBuf[idx+1] = targetVec.y;
          pBuf[idx+2] = targetVec.z;

          const maxFade = 0.85;
          const minFade = maxFade * Math.max(0.2, 1 - historyShadowStrength);
          const historyFade = u < 0.8 ? minFade + ((maxFade - minFade) * (u / 0.8)) : maxFade;

          cBuf[idx] = baseColor.r * historyFade;
          cBuf[idx+1] = baseColor.g * historyFade;
          cBuf[idx+2] = baseColor.b * historyFade;
       }
    }

    return {
       pts: ptsRef.current.subarray(0, finalPtsCount * 3),
       colors: colsRef.current.subarray(0, finalPtsCount * 3)
    };
  }, [data, startX, backChartWidth, historyShadowStrength, key, baseHex, getSmaHelixY, getSmaHelixZ]);
}

const SceneContent = function SceneContent({
  data,
  sliceOffset = 0,
  dataNewestAbs = NaN,
  seriesId = NaN,
  padLeft = 0,
  padRight = 0,
  visibleCount = 1000,
  setIsReturningToLive, setHistoryOffset, targetHistoryOffset,
  historyOffsetRef,
  hasReachedHistoryEnd,
  onOldestEdge,
  totalPoints,
  controlRotateSpeed,
  controlPanSpeed,
  controlZoomSpeed,
  show3D,
  onOpenMidWallChart, onOpenBackWallChart, onOpenFloorChart, onOpenSideChart,
  isBackWallChartOpen, isFloorChartOpen, isSideChartOpen,
  backwallStyle,
  showShadowFloor,
  showShadowSide,
  showMidLineWall,
  setShowMidLineWall,
  interactionMode,
  autoRotate,
  isCrosshairActive,
  setIsCrosshairActive,
  isCrosshairPinned,
  crosshairRef,
  useRealData,
  isLoadingData,
  isLoadingDataRef,
  timeframe,
  cameraTweenTarget,
  setCameraTweenTarget,
  controlsTweenTarget,
  setControlsTweenTarget,
  cameraStates,
  saveCameraState,
  zones,
  setZones,
  selectedZoneId,
  setSelectedZoneId,
  setInteractionMode,
  isReturningToLive,
  timeSensitivity,
  isRadialMenuOpen,
  width3D,
  heightScale3D,
  zLimit3D,
  setIsInitialGlideComplete,
  isInitialGlideComplete,
  isCurrentlyIdle,
  handleUserInteraction,
  scaleResetKey
}: { data: any[], [key: string]: any }) {
  const { camera } = useThree();
  syncCount(SC.SCENE_RENDER); // diagnóstico (?dbg=1): contador de renders da cena
  useP1RenderCommit(); // P1b T6(b): render → commit da cena (?dbg=1; sem a flag não usa hook)
  
  const colorUp = useFlowaesStore(state => state.colorUp);
  const colorDown = useFlowaesStore(state => state.colorDown);
  const colorIntensity = useFlowaesStore(state => state.colorIntensity);
  const colorContrast = useFlowaesStore(state => state.colorContrast);
  const midLineWallColor = useFlowaesStore(state => state.midLineWallColor);
  const midLineWallOpacity = useFlowaesStore(state => state.midLineWallOpacity);
  
  const momentumSensitivity = useFlowaesStore(state => state.momentumSensitivity);
  
  const lineWidth3D = useFlowaesStore(state => state.lineWidth3D);
  
  const showSMA10 = useFlowaesStore(state => state.showSMA10);
  const showSMA20 = useFlowaesStore(state => state.showSMA20);
  const showSMA50 = useFlowaesStore(state => state.showSMA50);
  const sma10Period = useFlowaesStore(state => state.sma10Period);
  const sma20Period = useFlowaesStore(state => state.sma20Period);
  const sma50Period = useFlowaesStore(state => state.sma50Period);
  
  const candleGraphDistance3D = useFlowaesStore(state => state.candleGraphDistance3D);
  const floorDistance3D = useFlowaesStore(state => state.floorDistance3D);
  const sideGraphDistance3D = useFlowaesStore(state => state.sideGraphDistance3D);
  const sideHistoryShadowStrength = useFlowaesStore(state => state.sideHistoryShadowStrength);
  const historyShadowStrength = useFlowaesStore(state => state.historyShadowStrength);
  
  const showGrid = useFlowaesStore(state => state.showGrid);
  const gridColor = useFlowaesStore(state => state.gridColor);
  const gridOpacity = useFlowaesStore(state => state.gridOpacity);
  const gridThickness = useFlowaesStore(state => state.gridThickness);
  
  const floorChartOpacity = useFlowaesStore(state => state.floorChartOpacity);
  const sideChartOpacity = useFlowaesStore(state => state.sideChartOpacity);
  const backChartOpacity = useFlowaesStore(state => state.backChartOpacity);
  const floorChartLineWidth = useFlowaesStore(state => state.floorChartLineWidth);
  const sideChartLineWidth = useFlowaesStore(state => state.sideChartLineWidth);
  const floorSmaOpacity = useFlowaesStore(state => state.floorSmaOpacity);
  const sideSmaOpacity = useFlowaesStore(state => state.sideSmaOpacity);
  const backSmaOpacity = useFlowaesStore(state => state.backSmaOpacity);
  const main3DOpacity = useFlowaesStore(state => state.main3DOpacity);
  const sma103DOpacity = useFlowaesStore(state => state.sma103DOpacity);
  const sma203DOpacity = useFlowaesStore(state => state.sma203DOpacity);
  const sma503DOpacity = useFlowaesStore(state => state.sma503DOpacity);
  const backSmaLineWidth = useFlowaesStore(state => state.backSmaLineWidth);
  const floorSmaLineWidth = useFlowaesStore(state => state.floorSmaLineWidth);
  const sideSmaLineWidth = useFlowaesStore(state => state.sideSmaLineWidth);
  const showConnectors = useFlowaesStore(state => state.showConnectors);
  const connectorsOpacity = useFlowaesStore(state => state.connectorsOpacity);
  const connectorsThickness = useFlowaesStore(state => state.connectorsThickness);
  const edgesColor = useFlowaesStore(state => state.edgesColor);
  const edgesOpacity = useFlowaesStore(state => state.edgesOpacity);
  const buyersColor = useFlowaesStore(state => state.buyersColor);
  const sellersColor = useFlowaesStore(state => state.sellersColor);
  
  const floorLabelsOpacity = useFlowaesStore(state => state.floorLabelsOpacity);
  const sideLabelsOpacity = useFlowaesStore(state => state.sideLabelsOpacity);
  const floorLabelsSize = useFlowaesStore(state => state.floorLabelsSize);
  const sideLabelsSize = useFlowaesStore(state => state.sideLabelsSize);
  const floorLabelsSpread = useFlowaesStore(state => state.floorLabelsSpread);
  const sideLabelsSpread = useFlowaesStore(state => state.sideLabelsSpread);
  const timeLabelsSize = useFlowaesStore(state => state.timeLabelsSize);
  const timeLabelsOpacity = useFlowaesStore(state => state.timeLabelsOpacity);
  const momentumLabelsSize = useFlowaesStore(state => state.momentumLabelsSize);
  const momentumLabelsOpacity = useFlowaesStore(state => state.momentumLabelsOpacity);
  const priceLabelsSize = useFlowaesStore(state => state.priceLabelsSize);
  const priceLabelsOpacity = useFlowaesStore(state => state.priceLabelsOpacity);
  const periodLabelsSize = useFlowaesStore(state => state.periodLabelsSize);
  const periodLabelsOpacity = useFlowaesStore(state => state.periodLabelsOpacity);
  
  const showPeriodSeparators = useFlowaesStore(state => state.showPeriodSeparators);
  const periodColor = useFlowaesStore(state => state.periodColor);
  const periodOpacity = useFlowaesStore(state => state.periodOpacity);
  const periodLineStyle = useFlowaesStore(state => state.periodLineStyle);
  const periodLineWidth = useFlowaesStore(state => state.periodLineWidth);
  const showPeriodLabels = useFlowaesStore(state => state.showPeriodLabels);
  const curveTension = useFlowaesStore(state => state.curveTension);
  const lastZoomDist = useRef(0);

  const controlsRef = useRef<any>(null);
  const dataXShiftGroupsRef = useRef<THREE.Group[]>([]);
  const addToShiftGroup = (el: THREE.Group | null) => {
    if (el && !dataXShiftGroupsRef.current.includes(el)) {
      dataXShiftGroupsRef.current.push(el);
    }
  };
  // Grupos da parede (dados adiados): deslocados com o offset do lote adiado
  const wallShiftGroupsRef = useRef<THREE.Group[]>([]);
  const addToWallShiftGroup = (el: THREE.Group | null) => {
    if (el && !wallShiftGroupsRef.current.includes(el)) {
      wallShiftGroupsRef.current.push(el);
    }
  };

  // Escala vertical visível: grupos cuja geometria foi desenhada com o domínio-base do lote principal
  // (minP/maxP) ou da parede (wallMinP/wallMaxP). A cada quadro recebem a transformação afim em Y que
  // leva esse domínio-base ao domínio desenhado pelo controlador (sem refazer vértices).
  const mainYGroupsRef = useRef<THREE.Group[]>([]);
  const addToMainYGroup = (el: THREE.Group | null) => {
    if (el && !mainYGroupsRef.current.includes(el)) mainYGroupsRef.current.push(el);
  };
  const wallYGroupsRef = useRef<THREE.Group[]>([]);
  const addToWallYGroup = (el: THREE.Group | null) => {
    if (el && !wallYGroupsRef.current.includes(el)) wallYGroupsRef.current.push(el);
  };

  // Profundidade (momentum): grupos cuja geometria usou o zScale-base do lote principal ou da parede
  const mainZGroupsRef = useRef<THREE.Group[]>([]);
  const addToMainZGroup = (el: THREE.Group | null) => {
    if (el && !mainZGroupsRef.current.includes(el)) mainZGroupsRef.current.push(el);
  };
  const wallZGroupsRef = useRef<THREE.Group[]>([]);
  const addToWallZGroup = (el: THREE.Group | null) => {
    if (el && !wallZGroupsRef.current.includes(el)) wallZGroupsRef.current.push(el);
  };
  const addToMainYZGroup = (el: THREE.Group | null) => { addToMainYGroup(el); addToMainZGroup(el); };
  const addToWallYZGroup = (el: THREE.Group | null) => { addToWallYGroup(el); addToWallZGroup(el); };

  // Diagnóstico (?dbg=1): refs dos grupos/linha do filamento e dos candles para a sonda de consistência
  const filGroupXRef = useRef<THREE.Group | null>(null);
  const filGroupYZRef = useRef<THREE.Group | null>(null);
  const candGroupXRef = useRef<THREE.Group | null>(null);
  const candGroupYRef = useRef<THREE.Group | null>(null);
  const filLineRef = useRef<THREE.Line | null>(null);
  const addFilGroupX = (el: THREE.Group | null) => { addToShiftGroup(el); filGroupXRef.current = el; };
  const addFilGroupYZ = (el: THREE.Group | null) => { addToMainYZGroup(el); filGroupYZRef.current = el; };
  const addCandGroupX = (el: THREE.Group | null) => { addToWallShiftGroup(el); candGroupXRef.current = el; };
  const addCandGroupY = (el: THREE.Group | null) => { addToWallYGroup(el); candGroupYRef.current = el; };

  const isFirstSessionLoad = useRef(true);
  const timeDragPointerXRef = useRef<number | null>(null);
  // P2a: o traço do arrasto do tempo pertence ao dedo que o começou (pointerId) e a referência (x) é fixada no down.
  // Encerram o traço: up/cancel/lostpointercapture desse dedo. O pointerout/leave dos meshes NÃO encerram um traço em
  // andamento: no r3f eles disparam dentro do e.stopPropagation() do outro mesh (troca de hover) e zeravam a referência
  // no meio da conta do movimento (X − null = X velas).
  const timeDragPointerIdRef = useRef<number>(NO_POINTER);
  const timeDragStrokeTRef = useRef<number>(0);
  const timeDragStroke = useRef<StrokeRefs>({ x: timeDragPointerXRef, pid: timeDragPointerIdRef, t: timeDragStrokeTRef }).current;
  const longPressPointerIdRef = useRef<number>(NO_POINTER);
  /** P2a: leave/out de troca de hover (traço em andamento e ainda há objeto sob o dedo) — não encerra o traço */
  const isHoverSwap = (e: any) => strokeActive(timeDragStroke) && !!(e && e.intersections && e.intersections.length > 0);
  if (touchRec) touchRec.refBox = timeDragPointerXRef; // P1c (?dbg=1): o registrador lê a referência do tratador do arrasto (só leitura)
  const timeDragVelocityRef = useRef<number>(0);
  const timeDragLastTimeRef = useRef<number>(0);
  const timeModeIdleTimerRef = useRef<any>(null);

  const cancelTimeModeIdleTimer = useMemo(() => mkCancelTimer(timeModeIdleTimerRef), []);
  const setInteractionModeRef = useRef(setInteractionMode);
  setInteractionModeRef.current = setInteractionMode;
  const startTimeModeIdleTimer = useMemo(() => mkStartTimer(timeModeIdleTimerRef, setInteractionModeRef, cancelTimeModeIdleTimer),
    [setInteractionMode, cancelTimeModeIdleTimer]);

  useEffect(() => {
    if (interactionMode !== 'time') {
      cancelTimeModeIdleTimer();
    }
  }, [interactionMode, cancelTimeModeIdleTimer]);

  useEffect(() => {
    return () => {
      cancelTimeModeIdleTimer();
    };
  }, [cancelTimeModeIdleTimer]);

  const hasTransitionedSavedInitial = useRef(false);
  const prevTimeframeRef = useRef<string>(timeframe);

  const [drawingZone, setDrawingZone] = useState<Partial<PriceZone> | null>(null);
  
  const [dragState, setDragState] = useState<{ 
    zoneId: string, 
    startPrice: number, 
    startTime: number, 
    startDepth: number, 
    initialPriceStart: number,
    initialPriceEnd: number,
    initialTimeStart: number,
    initialTimeEnd: number,
    initialDepthStart: number,
    initialDepthEnd: number,
    handle: '' | 'left' | 'right' | 'top' | 'bottom' | 'front' | 'back' 
  } | null>(null);
  const [showZoneSettingsId, setShowZoneSettingsId] = useState<string | null>(null);
  const [showMidWallOptions, setShowMidWallOptions] = useState(false);
  const [activePressPlane, setActivePressPlane] = useState<'back' | 'floor' | 'side' | null>(null);
  const longPressTimerRef = useRef<any>(null);

  useEffect(() => {
    // 1. If we are currently fetching real data, don't restore or reset yet.
    // The chart hasn't been updated.
    if (useRealData && isLoadingDataRef.current) return;

    // 2. Save the camera of the PREVIOUS timeframe before we reset the camera for the next timeframe
    const prevTf = prevTimeframeRef.current;
    if (prevTf && prevTf !== timeframe && camera && controlsRef.current) {
      saveCameraState(prevTf, 
        [camera.position.x, camera.position.y, camera.position.z],
        [controlsRef.current.target.x, controlsRef.current.target.y, controlsRef.current.target.z]
      );
    }

    const distZ = window.innerWidth < 768 ? 8000 : 6000;

    const isValidCameraState = (s: any) => {
      if (!s || !Array.isArray(s.position) || !Array.isArray(s.target)) return false;
      if (typeof s.position[0] !== 'number' || typeof s.position[1] !== 'number' || typeof s.position[2] !== 'number') return false;
      // Discard broken off-center positions (e.g. X > 1200 or Y > 1000)
      if (Math.abs(s.position[0]) > 1200 || Math.abs(s.position[1]) > 1000) return false;
      return true;
    };

    if (isFirstSessionLoad.current) {
      // FIRST load of the app session: Position camera directly in centered 3D perspective or valid saved state
      const saved = cameraStates[timeframe];
      if (isValidCameraState(saved)) {
        camera.position.set(saved.position[0], saved.position[1], saved.position[2]);
        if (controlsRef.current) {
          controlsRef.current.target.set(saved.target[0], saved.target[1], saved.target[2]);
          controlsRef.current.update();
        }
      } else {
        camera.position.set(0, 150, distZ);
        if (controlsRef.current) {
          controlsRef.current.target.set(0, 0, 0);
          controlsRef.current.update();
        }
      }
      isFirstSessionLoad.current = false;
      hasTransitionedSavedInitial.current = true;
      setIsInitialGlideComplete(true);
    } else {
      // Subsequent timeframe changes within the active session!
      hasTransitionedSavedInitial.current = true;
      const saved = cameraStates[timeframe];
      if (isValidCameraState(saved)) {
        camera.position.set(saved.position[0], saved.position[1], saved.position[2]);
        if (controlsRef.current) {
          controlsRef.current.target.set(saved.target[0], saved.target[1], saved.target[2]);
          controlsRef.current.update();
        }
      } else {
        camera.position.set(0, 150, distZ);
        if (controlsRef.current) {
          controlsRef.current.target.set(0, 0, 0);
          controlsRef.current.update();
        }
      }
    }

    // 4. Update timeframe ref for the next shift
    prevTimeframeRef.current = timeframe;
  }, [timeframe, camera, useRealData, isLoadingData]);

  // Automatically trigger the smooth transition to the saved camera state of the active session
  useEffect(() => {
    if (!hasTransitionedSavedInitial.current && !isLoadingData) {
      const timer = setTimeout(() => {
        if (!hasTransitionedSavedInitial.current) {
          hasTransitionedSavedInitial.current = true;
          const distZ = window.innerWidth < 768 ? 8000 : 6000;
          const saved = cameraStates[timeframe];
          const isValid = saved && Array.isArray(saved.position) && Array.isArray(saved.target) && Math.abs(saved.position[0]) <= 1200 && Math.abs(saved.position[1]) <= 1000;
          if (isValid) {
            setCameraTweenTarget(new THREE.Vector3(saved.position[0], saved.position[1], saved.position[2]));
            setControlsTweenTarget(new THREE.Vector3(saved.target[0], saved.target[1], saved.target[2]));
          } else {
            setCameraTweenTarget(new THREE.Vector3(0, 150, distZ));
            setControlsTweenTarget(new THREE.Vector3(0, 0, 0));
          }
        }
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [isLoadingData, timeframe, cameraStates, setCameraTweenTarget, setControlsTweenTarget, setIsInitialGlideComplete]);

  // Mark initial transition complete once glide is finished or user interacted
  useEffect(() => {
    if (isInitialGlideComplete) {
      hasTransitionedSavedInitial.current = true;
    }
  }, [isInitialGlideComplete]);

  useEffect(() => {
    // If the selected zone changes, close the settings menu
    setShowZoneSettingsId(null);
  }, [selectedZoneId]);

  const isCurrentlyDrawingOrMoving = !!drawingZone || dragState?.handle === '';

  useEffect(() => {
    if (isCurrentlyDrawingOrMoving) {
      // Smoothly transition to front-facing 2D view
      // We don't use position.lerp here because it's in a useEffect, 
      // but OrbitControls damping will make the target feel smoother.
      const distZ = window.innerWidth < 768 ? 8000 : 6000;
      camera.position.set(0, 0, distZ);
      if (controlsRef.current) {
        controlsRef.current.target.set(0, 0, 0);
        // Ensure damping is active for the transition
        controlsRef.current.enableDamping = true;
        controlsRef.current.update();
      }
    } else {
      // Restore to initial 3D position
      // camera.position.set(0, 150, 800); // We only do this if it's the VERY FIRST transition or a reset
    }
  }, [isCurrentlyDrawingOrMoving, camera]);

  useFrame((state, delta) => {
    // Apply time drag inertia removed per request

    appProfiler.tickFps();
    appProfiler.time('useFrame:SceneContent:ControlsTween', () => {
      if (cameraTweenTarget) {
        if (controlsRef.current) {
          controlsRef.current.enabled = false;
          controlsRef.current.enableDamping = false;
        }
        
        const smoothDelta = Math.min(delta, 0.1); 
        // Constant base speed + proportional speed guarantees we reach it
        const camDist = state.camera.position.distanceTo(cameraTweenTarget);
        
        const targetVec = controlsTweenTarget || [0, 0, 0];
        
        if (camDist < 0.2) {
          state.camera.position.copy(cameraTweenTarget);
          if (controlsRef.current) {
            controlsRef.current.target.copy(targetVec);
            controlsRef.current.enableDamping = true;
            controlsRef.current.update();
            controlsRef.current.enabled = interactionMode === 'orbit';
          }
          setCameraTweenTarget(null);
          setControlsTweenTarget(null);
          setIsInitialGlideComplete(true);
        } else {
          // Use a smoother lerp factor (proportional to framerate, but a bit faster to avoid taking too long at the end)
          const lerpFactor = 1 - Math.exp(-2.5 * smoothDelta);
          state.camera.position.lerp(cameraTweenTarget, lerpFactor);
          
          if (controlsRef.current) {
            controlsRef.current.target.lerp(targetVec, lerpFactor);
            controlsRef.current.update();
          }
        }
      } else {
        if (controlsRef.current && controlsRef.current.enabled) {
          controlsRef.current.update();
        }
      }
    });
  });

  useFrame(({ camera }) => {
    appProfiler.time('useFrame:SceneContent:ZoomDist', () => {
      const marginZ = heightScale3D * candleGraphDistance3D;
      const wallZ = -(zLimit3D + marginZ);
      const dist = Math.abs(camera.position.z - wallZ);
      
      if (Math.abs(dist - lastZoomDist.current) > (lastZoomDist.current * 0.15) || lastZoomDist.current === 0) {
        lastZoomDist.current = dist;
        
        const fov = (camera as THREE.PerspectiveCamera).fov;
        if (fov) {
          const visibleHeight = 2 * Math.tan((fov * Math.PI) / 360) * dist;
          const visibleWidth = visibleHeight * (camera as THREE.PerspectiveCamera).aspect;
          
        }
      }
    });
  });
  
    const longPressBackWallStartRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const longPressFloorStartRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const longPressSideStartRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const longPressMidWallStartRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
  const lastTapTimeRef = useRef(0);
  const clickCountRef = useRef(0);
    const pressIntentTimerRef = useRef<any>(null);

  // Subscribe to gestureVersion to cancel pending back wall long press when another gesture is detected
  const gestureVersion = useFlowaesStore((state) => state.gestureVersion);
  useEffect(() => {
    const activeGesture = useFlowaesStore.getState()?.activeGesture;
    
  }, [gestureVersion]);

  const width = width3D;
  
  // Spacing between the Candles chart and the price scale axis on the Back Wall
  const backChartSpacing = Math.max(130, width * 0.18);
  const backChartWidth = width - backChartSpacing;
  
  const startX = -width3D / 2; 

  const effectiveVisibleCount = Math.min(visibleCount, Math.max(2, data.length));
  const stepX = backChartWidth / Math.max(1, effectiveVisibleCount - 1);
  const bufferStartX = startX - padLeft * stepX;
  const bufferWidth = Math.max(1, data.length - 1) * stepX;

  // A parede usa uma cópia ADIADA dos dados. O lote (offset e sobras) vai junto na mesma cópia
  // adiada, para o deslocamento da parede sempre combinar com as velas que ela desenha.
  // F2: o lote leva o índice ancorado da vela mais nova da série de onde saiu (a cópia adiada da parede também)
  const batchWindow = useMemo(() => ({ data, sliceOffset, padLeft, padRight, newestAbs: dataNewestAbs, seriesId }), [data, sliceOffset, padLeft, padRight, dataNewestAbs, seriesId]);
  const deferredWall = React.useDeferredValue(batchWindow);
  // F3b: a parede nunca desenha a série ANTERIOR (cópia adiada de outra série → lote atual; ver wallBatch.ts)
  const wallWindow = pickWallBatch(deferredWall, batchWindow);
  const wallData = wallWindow.data;
  const wallStepX = backChartWidth / Math.max(1, effectiveVisibleCount - 1);
  const wallBufferStartX = startX - wallWindow.padLeft * wallStepX;
  const wallBufferWidth = Math.max(1, wallData.length - 1) * wallStepX;

  const { fullColors3D, adjustedColorUp, adjustedColorDown } = useWaveColors(data, colorUp, colorDown, colorIntensity, colorContrast);
  
  // Phase A: Deferred colors and geometry to match deferred wallData
  const { fullColors3D: fullColors3DWall } = useWaveColors(wallData, colorUp, colorDown, colorIntensity, colorContrast);
  
  const layout = useMemo(() => ({
    width3D: bufferWidth,
    heightScale3D: heightScale3D,
    zLimit3D: zLimit3D,
    startX: bufferStartX,
  }), [bufferWidth, heightScale3D, zLimit3D, bufferStartX]);

  const wallLayout = useMemo(() => ({
    width3D: wallBufferWidth,
    heightScale3D: heightScale3D,
    zLimit3D: zLimit3D,
    startX: wallBufferStartX,
  }), [wallBufferWidth, heightScale3D, zLimit3D, wallBufferStartX]);

  const wallConfig = useMemo(() => ({
    momentumSensitivity,
    curveTension,
    historyShadowStrength,
    sideHistoryShadowStrength,
    sma10Period,
    sma20Period,
    sma50Period,
    showSMA10,
    showSMA20,
    showSMA50,
    fullColors3D: fullColors3DWall,
  }), [momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength, sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50, fullColors3DWall]);

  const {
    smoothedPoints: wallSmoothedPoints,
    smoothedColorsSide: wallSmoothedColorsSide,
    smoothedColorsFloor: wallSmoothedColorsFloor,
    smoothedPtsCount: wallSmoothedPtsCount,
    minP: wallMinP,
    maxP: wallMaxP,
    getY: wallGetY,
    zScale: wallZScale
  } = useWaveGeometry(wallData, wallLayout, wallConfig, 1);

  const getWallForceZ = useMemo(() => mkScaleFn(wallZScale), [wallZScale]);

  const config = useMemo(() => ({
    momentumSensitivity,
    curveTension,
    historyShadowStrength,
    sideHistoryShadowStrength,
    sma10Period,
    sma20Period,
    sma50Period,
    showSMA10,
    showSMA20,
    showSMA50,
    fullColors3D,
  }), [momentumSensitivity, curveTension, historyShadowStrength, sideHistoryShadowStrength, sma10Period, sma20Period, sma50Period, showSMA10, showSMA20, showSMA50, fullColors3D]);

  const {
    minP, maxP, maxMom, priceToPixelScale, zScale,
    getY, getPrice, getTime, getHelixY, getHelixZ, getSmaHelixY, getSmaHelixZ,
    decimatedPoints, decimatedColors, finalLivePos, finalLiveColor, finalColorRGB,
    smoothedPoints, smoothedColors3D, smoothedColorsSide, smoothedColorsFloor, smoothedPtsCount
  } = useWaveGeometry(data, layout, config, 0);

  // Telemetria passiva (?dbg=1): só registra valores já calculados (a cada render, não por quadro)
  if (DBG_ENABLED) {
    dbgScene({
      data, minP, maxP, wallMinP, wallMaxP, wallStale: wallData !== data,
      padLeft, padRight, sliceOffset, wallSliceOffset: wallWindow.sliceOffset, visibleCount, newestAbs: dataNewestAbs,
      showSMA10, showSMA20, showSMA50,
    });
  }
  
  // Memoize vectors specifically so we don't break reference on each scroll
  
  const currentPoints3D = useMemo(() => show3D ? decimatedPoints : [], [show3D, decimatedPoints]);
  
  const visibleStartIndex = padLeft;
  const visibleEndIndex = Math.min(data.length - 1, visibleStartIndex + (visibleCount || 1000) - 1);

  const wallVisibleStartIndex = padLeft;
  const wallVisibleEndIndex = Math.min(wallData.length - 1, wallVisibleStartIndex + (visibleCount || 1000) - 1);

  // Harness de replay (F3a.1 C3): horário da vela mais nova que a parede desenha na janela visível (lote commitado)
  useEffect(() => { if (replay) replay.wallLastT = wallData[wallData.length - 1 - wallWindow.padRight]?.timestamp ?? null; }, [wallData, wallWindow.padRight]);

  const wallDataVisible = useMemo(() => {
    if (!wallData || wallData.length === 0) return [];
    return wallData.slice(wallVisibleStartIndex, wallVisibleEndIndex + 1);
  }, [wallData, wallVisibleStartIndex, wallVisibleEndIndex]);

  const timeMin = (data.length > 0 && data[visibleStartIndex]?.timestamp) ? data[visibleStartIndex].timestamp! : ((data.length > 0 && data[0]?.timestamp) ? data[0].timestamp! : Date.now() - 24 * 3600 * 1000);
  const timeMax = (data.length > 0 && data[visibleEndIndex]?.timestamp) ? data[visibleEndIndex].timestamp! : ((data.length > 0 && data[data.length - 1]?.timestamp) ? data[data.length - 1].timestamp! : Date.now());
  
  // Bug 1 Fix: Dynamically calculate the future time representing the empty space padding on the right.
  // This updates continuously as new candles arrive in real-time.
  const extendedTimeMax = useMemo(() => {
    if (timeMax <= timeMin || backChartWidth <= 0) return timeMax;
    const timePerPixel = (timeMax - timeMin) / backChartWidth;
    return timeMax + (width - backChartWidth) * timePerPixel;
  }, [timeMin, timeMax, backChartWidth, width]);

  // Keep getTimeX physically accurate: map future times linearly across the extended area
  const getTimeX = useMemo(() => mkTimeXFn(timeMin, timeMax, backChartWidth, startX), [timeMin, timeMax, backChartWidth, startX]);

  const wallTimeMin = (wallData.length > 0 && wallData[wallVisibleStartIndex]?.timestamp) ? wallData[wallVisibleStartIndex].timestamp! : ((wallData.length > 0 && wallData[0]?.timestamp) ? wallData[0].timestamp! : Date.now() - 24 * 3600 * 1000);
  const wallTimeMax = (wallData.length > 0 && wallData[wallVisibleEndIndex]?.timestamp) ? wallData[wallVisibleEndIndex].timestamp! : ((wallData.length > 0 && wallData[wallData.length - 1]?.timestamp) ? wallData[wallData.length - 1].timestamp! : Date.now());

  const wallExtendedTimeMax = useMemo(() => {
    if (wallTimeMax <= wallTimeMin || backChartWidth <= 0) return wallTimeMax;
    const timePerPixel = (wallTimeMax - wallTimeMin) / backChartWidth;
    return wallTimeMax + (width - backChartWidth) * timePerPixel;
  }, [wallTimeMin, wallTimeMax, backChartWidth, width]);

  const wallGetTimeX = useMemo(() => mkTimeXFn(wallTimeMin, wallTimeMax, backChartWidth, startX), [wallTimeMin, wallTimeMax, backChartWidth, startX]);

  const forceMin = -zLimit3D / zScale;
  const forceMax = zLimit3D / zScale;
  const getForceZ = useMemo(() => mkScaleFn(zScale), [zScale]);

  const wallForceMin = -zLimit3D / wallZScale;
  const wallForceMax = zLimit3D / wallZScale;

  const zLimit = zLimit3D;
  const heightScale = heightScale3D;

  const marginZ = heightScale * candleGraphDistance3D;
  const floorY = -(heightScale / 2 + heightScale * floorDistance3D);
  const wallZ = -(zLimit + marginZ);
  const sideX = startX - width * sideGraphDistance3D;
  const axisX = startX + width;
  const topY = (heightScale / 2) + heightScale * 0.1;
  const frontZ = zLimit;
  
  // Create evenly spaced lines for bounded custom grids
const priceLabels = useMemo(() => {
    const range = maxP - minP;
    if (range === 0) return [{ price: minP, level: 0 }];
    
    const calculateTicks = (targetCount: number) => {
        const rawStep = range / targetCount;
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalizedStep = rawStep / magnitude;
        
        let step;
        if (normalizedStep < 1.5) step = 1 * magnitude;
        else if (normalizedStep < 3) step = 2 * magnitude;
        else if (normalizedStep < 7) step = 5 * magnitude;
        else step = 10 * magnitude;
        
        const start = Math.ceil(minP / step) * step;
        const end = Math.floor(maxP / step) * step;
        
        const ticks = [];
        for (let p = start; p <= end + (step / 100); p += step) {
            ticks.push(p);
        }
        return ticks;
    };

    const majorTicks = calculateTicks(8);
    const minorTicks = calculateTicks(20);
    const microTicks = calculateTicks(50);

    const labels: {price: number, level: number}[] = [];
    
    const addTick = (p: number, level: number) => {
        if (!labels.some(l => Math.abs(l.price - p) < (range / 1000))) {
            labels.push({ price: p, level });
        }
    };
    
    majorTicks.forEach(p => addTick(p, 0));
    minorTicks.forEach(p => addTick(p, 1));
    microTicks.forEach(p => addTick(p, 2));

    return labels;
  }, [minP, maxP]);
  const gridLinesX = useMemo(() => Array.from({length: 17}, (_, i) => startX + (i / 16) * width), [startX, width]);
  const chartGridLinesY = useMemo(() => priceLabels.filter(l => l.level <= 1).map(l => ({ y: getY(l.price), level: l.level })), [priceLabels, getY]);
  // Bordas verticais FIXAS da parede (recorte e moldura): o domínio desenhado sempre ocupa [-H/2, H/2],
  // então as bordas não dependem mais das linhas de preço (que antes mudavam a cada troca de lote).
  const bottomOfGrid = -heightScale / 2;
  const topOfGrid = heightScale / 2;

  // Per-wall WebGL clipping planes to prevent 3D geometry and tracker leakage outside grid boundaries
  const backChartClippingPlanes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), -startX),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), startX + backChartWidth),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), -bottomOfGrid),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), topOfGrid),
  ], [startX, backChartWidth, bottomOfGrid, topOfGrid]);

  const backClippingPlanes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), -startX),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), startX + width),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), -bottomOfGrid),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), topOfGrid),
  ], [startX, width, bottomOfGrid, topOfGrid]);

  const floorClippingPlanes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), -startX),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), startX + backChartWidth),
    new THREE.Plane(new THREE.Vector3(0, 0, 1), zLimit),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), zLimit),
  ], [startX, backChartWidth, zLimit]);

  const sideClippingPlanes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(0, 0, 1), zLimit),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), zLimit),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), -bottomOfGrid),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), topOfGrid),
  ], [zLimit, bottomOfGrid, topOfGrid]);

  // Unified 6-plane volumetric clipping for central 3D wave and spatial SMAs, reusing the exact domains of the walls
  const central3DClippingPlanes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), -startX),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), startX + backChartWidth),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), -bottomOfGrid),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), topOfGrid),
    new THREE.Plane(new THREE.Vector3(0, 0, 1), zLimit),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), zLimit),
  ], [startX, backChartWidth, bottomOfGrid, topOfGrid, zLimit]);

  const getDepth = useMemo(() => mkDepthFn(wallZ, frontZ), [wallZ, frontZ]);

  useEffect(() => {
    const handleGlobalUp = (e: PointerEvent) => {
      const activeGesture = useFlowaesStore.getState().activeGesture;
      if (activeGesture === 'time_drag') {
        if (!strokeEnd(timeDragStroke, e.pointerId)) return; // P2a: o up de outro dedo não encerra o traço
        useFlowaesStore.getState().setActiveGesture(null);
        if (typeof setInteractionMode === 'function') {
          setInteractionMode('orbit');
        }
      }
    };
    // P2a: cancelamento do toque ou perda da captura encerram o traço do mesmo dedo (antes era o pointerleave do mesh,
    // via r3f, que fazia isso; agora o leave não encerra um traço em andamento)
    const handleGlobalCancel = (e: PointerEvent) => {
      if (!strokeOwns(timeDragStroke, e.pointerId)) return;
      strokeEnd(timeDragStroke, e.pointerId);
      if (useFlowaesStore.getState().activeGesture === 'time_drag') useFlowaesStore.getState().setActiveGesture(null);
      startTimeModeIdleTimer();
    };
    window.addEventListener('pointerup', handleGlobalUp);
    window.addEventListener('pointercancel', handleGlobalCancel);
    window.addEventListener('lostpointercapture', handleGlobalCancel);
    return () => {
      window.removeEventListener('pointerup', handleGlobalUp);
      window.removeEventListener('pointercancel', handleGlobalCancel);
      window.removeEventListener('lostpointercapture', handleGlobalCancel);
    };
  }, [setInteractionMode, startTimeModeIdleTimer]);

  const [_resolution, setResolution] = useState(new THREE.Vector2(window.innerWidth, window.innerHeight));

  useEffect(() => {
    const handleResize = () => setResolution(new THREE.Vector2(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to adjust color based on intensity and contrast
  

  // Projections using full data but matching colors
   


  

  const pointsSideRef = useRef<Float32Array>(new Float32Array(3000));
  const pointsSide  = useMemo(() => {
    const srcLen = smoothedPtsCount > 0 ? smoothedPtsCount : data.length;
    const requiredLength = srcLen * 3;
    
    if (requiredLength > pointsSideRef.current.length) {
      let newCap = pointsSideRef.current.length * 2;
      while (newCap < requiredLength) newCap *= 2;
      pointsSideRef.current = new Float32Array(newCap);
    }
    const buffer = pointsSideRef.current;
    
    if (smoothedPtsCount > 0) {
      for (let i = 0; i < smoothedPtsCount; i++) {
        const idx = i * 3;
        buffer[idx] = sideX;
        buffer[idx+1] = smoothedPoints[idx+1];
        buffer[idx+2] = smoothedPoints[idx+2];
      }
    } else {
      for (let i = 0; i < data.length; i++) {
        buffer[i * 3] = sideX;
        buffer[i * 3 + 1] = getHelixY(data[i]);
        buffer[i * 3 + 2] = getHelixZ(data[i]);
      }
    }
    return buffer.subarray(0, requiredLength);
  }, [smoothedPoints, data, sideX, minP, maxP, startX, width]);

  const smaPointsFloorRef = useRef<Float32Array>(new Float32Array(3000));
  

  const sma20PointsFloorRef = useRef<Float32Array>(new Float32Array(3000));
  

  const sma50PointsFloorRef = useRef<Float32Array>(new Float32Array(3000));
  

  
  
  
  
  const { pts: sma10Pts, colors: sma10Cols } = useSma3DBuffers('sma10', '#facc15', data, bufferStartX, bufferWidth, historyShadowStrength, getSmaHelixY, getSmaHelixZ);
  const { pts: sma20Pts, colors: sma20Cols } = useSma3DBuffers('sma20', '#38bdf8', data, bufferStartX, bufferWidth, historyShadowStrength, getSmaHelixY, getSmaHelixZ);
  const { pts: sma50Pts, colors: sma50Cols } = useSma3DBuffers('sma50', '#a855f7', data, bufferStartX, bufferWidth, historyShadowStrength, getSmaHelixY, getSmaHelixZ);

  const candlesBack = useMemo(() => {
    const candles = [];
    
    if (wallData.length > 0 && wallData[0].open !== undefined) {
      let hasValid = false;
      const len = wallData.length;
      for (let i = 0; i < len; i++) {
        const d = wallData[i];
        if ((d as any).isRealCandle !== false && d.open !== undefined) {
          hasValid = true;
          break;
        }
      }
      
      if (hasValid) {
        const maxIdx = Math.max(1, len - 1);
        for (let i = 0; i < len; i++) {
          const d = wallData[i];
          if ((d as any).isRealCandle !== false && d.open !== undefined) {
            candles.push({
              open: d.open!,
              close: d.close!,
              high: d.high!,
              low: d.low!,
              time: i / maxIdx
            });
          }
        }
        return candles;
      }
    }

    const numCandles = Math.min(200, Math.floor(wallData.length / 2));
    const chunkSize = Math.max(1, Math.floor(wallData.length / numCandles));
    
    for (let i = 0; i < numCandles; i++) {
      const startIdx = i * chunkSize;
      const endIdx = Math.min(startIdx + chunkSize, wallData.length);
      
      if (endIdx <= startIdx) continue;
      
      const open = wallData[startIdx].price;
      const close = wallData[endIdx - 1].price;
      let high = wallData[startIdx].price;
      let low = wallData[startIdx].price;
      
      for (let j = startIdx + 1; j < endIdx; j++) {
        const p = wallData[j].price;
        if (p > high) high = p;
        if (p < low) low = p;
      }
      
      const chunkLen = endIdx - startIdx;
      const time = Math.floor(chunkLen / 2) / Math.max(1, chunkLen - 1);
      
      candles.push({ open, close, high, low, time });
    }
    return candles;
  }, [wallData]);



  
  // Combine tick lines for InstancedMesh to reduce draw calls
  const tickLinesData = useMemo(() => {
    if (wallData !== data) return []; // Stagger
    const lines: { start: [number, number, number], end: [number, number, number], color?: string, thickness?: number, opacity?: number }[] = [];
    
    // Price Ticks (Right Side)
    priceLabels.forEach(tick => {
      const y = getY(tick.price);
      const direction = 1; // isLeft = false
      const len = direction * (tick.level === 0 ? 12 : (tick.level === 1 ? 8 : 5));
      const opacity = tick.level === 0 ? 0.6 : (tick.level === 1 ? 0.4 : 0.2);
      const color = "#00d9ff";
      lines.push({ start: [startX + width + 50, y, wallZ], end: [startX + width + 50 + len, y, wallZ], color, opacity, thickness: tick.level === 0 ? 2 : 1.5 });
    });

    // Time Ticks (Floor)
    // Removed visual connectors from the 0-momentum axis to the label per user request.

    return lines;
  }, [priceLabels, data, startX, width, getY, wallZ, frontZ, floorY]);

  const periodSeparators = useMemo(() => {
    if (!showPeriodSeparators || data.length < 2) return [];
    
    const sepIndices = getSeparatorIndices(data, timeframe);
    const separators = [];
    
    for (const sep of sepIndices) {
      const posX = startX + (sep.index / Math.max(1, (data.length - 1))) * backChartWidth;
      if (isWithin1DBounds(posX, startX, startX + backChartWidth)) {
        separators.push({
          index: sep.index,
          x: posX,
          label: sep.label
        });
      }
    }
    
    return separators;
  }, [data, timeframe, showPeriodSeparators, periodColor, periodOpacity, startX, backChartWidth]);

  const colorUpStyle = useMemo(() => `#${adjustedColorUp.getHexString()}`, [adjustedColorUp]);
  const colorDownStyle = useMemo(() => `#${adjustedColorDown.getHexString()}`, [adjustedColorDown]);

  const connectorsRef = useRef<{positions: Float32Array, colors: Float32Array, length: number}>({
    positions: new Float32Array(1200),
    colors: new Float32Array(1200),
    length: 0
  });

  const connectorsTrigger = `${data[0]?.timestamp}-${data.length}-${minP}-${maxP}-${bufferStartX}-${bufferWidth}-${floorY}-${wallZ}`;

  const { connectorsPos, connectorsCol, connectorsLen } = useMemo(() => {
    if (!showConnectors) return { connectorsPos: null, connectorsCol: null, connectorsLen: 0 };

    const step = Math.max(1, Math.floor(data.length / 30)); 
    const numConnectors = Math.floor(data.length / step) * 2;
    const requiredLength = numConnectors * 6; // 2 points per line * 3 coordinates

    if (requiredLength > connectorsRef.current.positions.length) {
      const newCap = Math.max(requiredLength * 2, 1200);
      connectorsRef.current.positions = new Float32Array(newCap);
      connectorsRef.current.colors = new Float32Array(newCap);
    }

    const pos = connectorsRef.current.positions;
    const col = connectorsRef.current.colors;
    
    const cUp = new THREE.Color(colorUpStyle);
    const cDown = new THREE.Color(colorDownStyle);

    let idx = 0;
    for (let i = 0; i < wallData.length; i += step) {
        const d = wallData[i];
        if (!d) continue;

        const isUp = d.open !== undefined ? d.close! >= d.open! : (i > 0 ? d.price >= wallData[i - 1].price : true);
        const color = isUp ? cUp : cDown;

        const x = wallBufferStartX + (i / Math.max(1, (wallData.length - 1))) * wallBufferWidth;
        const hy = getHelixY(d);
        const hz = getHelixZ(d);
        const py = getY(d.price);

        // --- Back Connector ---
        pos[idx * 3] = x;
        pos[idx * 3 + 1] = hy;
        pos[idx * 3 + 2] = hz;
        col[idx * 3] = color.r;
        col[idx * 3 + 1] = color.g;
        col[idx * 3 + 2] = color.b;
        idx++;

        pos[idx * 3] = x;
        pos[idx * 3 + 1] = py;
        pos[idx * 3 + 2] = wallZ;
        col[idx * 3] = color.r;
        col[idx * 3 + 1] = color.g;
        col[idx * 3 + 2] = color.b;
        idx++;

        // --- Floor Connector ---
        pos[idx * 3] = x;
        pos[idx * 3 + 1] = hy;
        pos[idx * 3 + 2] = hz;
        col[idx * 3] = color.r;
        col[idx * 3 + 1] = color.g;
        col[idx * 3 + 2] = color.b;
        idx++;

        pos[idx * 3] = x;
        pos[idx * 3 + 1] = floorY;
        pos[idx * 3 + 2] = hz;
        col[idx * 3] = color.r;
        col[idx * 3 + 1] = color.g;
        col[idx * 3 + 2] = color.b;
        idx++;
    }

    connectorsRef.current.length = idx;
    return {
        connectorsPos: pos.subarray(0, idx * 3),
        connectorsCol: col.subarray(0, idx * 3),
        connectorsLen: idx
    };
  }, [connectorsTrigger, wallData, colorUpStyle, colorDownStyle, getHelixY, getHelixZ, getY, showConnectors]);

  const calcIndex = (x: number) => {
    if (!data || data.length === 0) return 0;
    return Math.max(0, Math.min(data.length - 1, Math.round(((x - bufferStartX) / bufferWidth) * (data.length - 1))));
  }

  const [autoRotateEnabled, setAutoRotateEnabled] = useState(false);

  useEffect(() => {
    setAutoRotateEnabled(
      interactionMode === 'orbit' && 
      autoRotate && 
      !isCrosshairActive && 
      isInitialGlideComplete && 
      !cameraTweenTarget &&
      isCurrentlyIdle
    );
  }, [interactionMode, autoRotate, isCrosshairActive, isInitialGlideComplete, cameraTweenTarget, isCurrentlyIdle]);

  // Diagnóstico (?dbg=1): registra UMA vez por encontrão o gesto limitado à borda carregada (sem custo sem a flag)
  const edgeHitRef = useRef(false);
  const oldestEdgeRef = useRef(false);   // F3e C4
  const noteEdgeClamp = (wanted: number, total: number) => {
    const edge = Math.max(0, total - 1000);
    if (wanted > edge + 0.5) {
      if (!edgeHitRef.current) { edgeHitRef.current = true; dbgEvent(`gesto: alvo limitado à borda (carregado ${edge} velas; dedo queria ${wanted.toFixed(0)})`); }
    } else if (wanted < edge - 2) edgeHitRef.current = false;
  };

  // Arrasto do tempo: o movimento do dedo (ganho 1,00 aplicado antes) vira alvo, travado à borda carregada (F0).
  // Um só caminho para os dois handlers de toque e para o harness de replay (tests/replay).
  const applyTimeDrag = (offsetMovement: number) => {
    // F3a.1 C1: o arrasto lê e escreve o alvo no estado ATUAL da navegação, com a borda guardada nela (índice ancorado,
    // setLimitFor). Antes era relativo à série deste render (props): entre a entrega de um bloco mais novo
    // (commitProcessed) e o render com ele, um movimento que batia no 0 da série velha virava "presente" da série
    // nova e o alvo saltava o bloco inteiro (937 velas). Com as props em dia as duas contas dão o mesmo número.
    const nav = (targetHistoryOffset as any).nav;
    const lim = nav ? nav.limitDistance : null;
    const total = lim !== null ? lim + NAV_VISIBLE_CANDLES : totalPoints;
    const cur = nav ? targetHistoryOffset.current : targetFor(targetHistoryOffset, dataNewestAbs);
    const wanted = cur + offsetMovement;
    const clamped = clampDragTarget(cur, offsetMovement, total);
    if (DBG_ENABLED) navSource(SRC.ARRASTO);
    if (nav) targetHistoryOffset.current = clamped; else setTargetFor(targetHistoryOffset, clamped, dataNewestAbs);
    // P1b (?dbg=1, só anota): Δ pedido pelo dedo, Δ aplicado e a trava que atuou
    if (DBG_ENABLED) { navSource(0); navDrag(offsetMovement, clamped - cur, wanted < 0 ? (navRec!.atPresent ? LIM.PRESENTE : LIM.NOVA) : wanted > maxNavOffset(total) ? LIM.ANTIGA : LIM.NENHUMA); }
    // diagnóstico: o detector de RECUO compara em índice ancorado (a vela do alvo); o pedido do dedo vai na mesma conta
    // F3e C4: encostou na borda mais antiga carregada (uma vez por encostada): o App decide se é o início dos dados
    { const edge = Math.max(0, total - NAV_VISIBLE_CANDLES);
      if (wanted > edge + 0.5) { if (!oldestEdgeRef.current) { oldestEdgeRef.current = true; if (typeof onOldestEdge === 'function') onOldestEdge(); } }
      else if (wanted < edge - 2) oldestEdgeRef.current = false; }
    if (DBG_ENABLED) { noteEdgeClamp(wanted, total); if (nav) dbgDragWrite(nav.targetIndex + (wanted - clamped), nav.targetIndex); else dbgDragWrite(wanted, clamped); }
  };
  if (replay) replay.drag = applyTimeDrag;

  // Lote que está NA GPU (C1). Publicado por um efeito passivo desta cena, que roda depois dos efeitos dos filhos
  // (FastLine / InstancedCandles = o upload). Os useFrame de deslocamento, escala e recorte leem daqui, nunca das
  // props do render, para não aplicar o deslocamento/escala do lote novo sobre a geometria antiga. Ver uploadedBatch.ts.
  const uploadedRef = useRef<UploadedBatch | null>(null);

  useFrame(() => {
    const U = uploadedRef.current;
    if (U && historyOffsetRef?.current !== undefined && U.data && U.data.length > 1) {
      // posição relativa a cada lote (F2): o principal e o da parede (adiado) podem ter velas mais novas diferentes
      const currentOffset = positionFor(historyOffsetRef, U.newestAbs);
      // Clamping between -padRight (hard anchor at present/right boundary) and padLeft (past/left boundary)
      const smoothDiff = computeSliceShift(currentOffset, U.sliceOffset, U.padLeft, U.padRight);
      dataXShiftGroupsRef.current.forEach(g => {
        g.position.x = smoothDiff * U.stepX;
      });
      // Parede: mesmo cálculo, mas com o lote que ela realmente está desenhando
      const wallShift = computeSliceShift(positionFor(historyOffsetRef, U.wallNewestAbs), U.wallSliceOffset, U.wallPadLeft, U.wallPadRight);
      wallShiftGroupsRef.current.forEach(g => {
        g.position.x = wallShift * U.wallStepX;
      });
    }
  });

  // ---- Escala vertical visível (Item 2) ----
  // Estado único da escala do cubo: o controlador decide o domínio de preço desenhado a cada quadro
  // (só velas visíveis + SMAs ativas, margem, suavização por tempo, sem cortar vela). Rótulos, crosshair
  // e trackers leem scaleStateRef. Nada aqui chama setState.
  const scaleCtrlRef = useRef<VisibleScaleController | null>(null);
  if (scaleCtrlRef.current === null) scaleCtrlRef.current = new VisibleScaleController();
  const scaleVisRef = useRef<Int32Array | null>(null);
  if (scaleVisRef.current === null) scaleVisRef.current = new Int32Array(2);
  const scaleAffRef = useRef<Float64Array | null>(null);
  if (scaleAffRef.current === null) scaleAffRef.current = new Float64Array(2);
  const scaleSmaRef = useRef({ sma10: false, sma20: false, sma50: false });
  const scaleFrameRef = useRef({ lastT: 0, lastOffset: NaN });
  /** Domínio desenhado e transformações (y_desenhado = A * y_base + C) dos lotes principal e da parede. */
  const scaleStateRef = useRef({ ready: false, dMin: 0, dMax: 1, mainA: 1, mainC: 0, wallA: 1, wallC: 0, zScale: 1, mainZK: 1, wallZK: 1 });
  // Profundidade Z só pelo momentum visível, dentro do cubo com folga de 5%
  const depthCtrlRef = useRef<VisibleDepthController | null>(null);
  if (depthCtrlRef.current === null) depthCtrlRef.current = new VisibleDepthController();
  const depthWeightsRef = useRef({ sma10: 0, sma20: 0, sma50: 0 });
  // Troca de ativo/timeframe: a escala salta (sem animação) até os dados novos chegarem (no máximo 5 s)
  const scaleResetRef = useRef<{ data: any[] | null; t: number }>({ data: null, t: 0 });
  const dataForResetRef = useRef<any[]>(data);
  dataForResetRef.current = data;
  useEffect(() => {
    scaleResetRef.current = { data: dataForResetRef.current, t: performance.now() };
    scaleCtrlRef.current!.reset();
    depthCtrlRef.current!.reset();
  }, [scaleResetKey]);

  useFrame(() => {
    const ctrl = scaleCtrlRef.current!;
    const U = uploadedRef.current;
    if (!U || !historyOffsetRef || U.data.length < 2) return;
    const now = performance.now();
    const F = scaleFrameRef.current;
    const dt = F.lastT ? now - F.lastT : 0;
    // velocidade pelo movimento da navegação (vela nova ao vivo não conta); faixa pela posição relativa ao lote
    const mv = motionOf(historyOffsetRef);
    const velocity = dt > 0 && F.lastOffset === F.lastOffset ? ((mv - F.lastOffset) / dt) * 1000 : 0;
    F.lastT = now;
    F.lastOffset = mv;
    const cur = positionFor(historyOffsetRef, U.newestAbs);
    const R = scaleResetRef.current;
    if (R.data) {
      if (U.data !== R.data || now - R.t > 5000) R.data = null;
      ctrl.reset();
      depthCtrlRef.current!.reset();
    }
    const vis = scaleVisRef.current!;
    visibleBatchRange(U.padLeft, computeSliceShift(cur, U.sliceOffset, U.padLeft, U.padRight), effectiveVisibleCount, U.data.length, vis);
    const sma = scaleSmaRef.current;
    sma.sma10 = !!showSMA10; sma.sma20 = !!showSMA20; sma.sma50 = !!showSMA50;
    const tCalc = DBG_ENABLED ? performance.now() : 0;
    ctrl.update(U.data as any[], vis[0], vis[1], velocity, dt, sma);
    if (DBG_ENABLED) dbgScaleCalc(performance.now() - tCalc);
    if (!ctrl.ready) return;

    const S = scaleStateRef.current;
    const aff = scaleAffRef.current!;
    yAffine(U.minP, U.maxP, ctrl.dMin, ctrl.dMax, U.heightScale3D, aff);
    S.mainA = aff[0]; S.mainC = aff[1];
    const mg = mainYGroupsRef.current;
    for (let i = 0; i < mg.length; i++) { mg[i].scale.y = aff[0]; mg[i].position.y = aff[1]; }
    yAffine(U.wallMinP, U.wallMaxP, ctrl.dMin, ctrl.dMax, U.heightScale3D, aff);
    S.wallA = aff[0]; S.wallC = aff[1];
    const wg = wallYGroupsRef.current;
    for (let i = 0; i < wg.length; i++) { wg[i].scale.y = aff[0]; wg[i].position.y = aff[1]; }
    S.dMin = ctrl.dMin; S.dMax = ctrl.dMax; S.ready = true;

    // Profundidade: Z desenhado = momentum × zScale do controlador; os grupos recebem a razão sobre o zScale-base
    const depth = depthCtrlRef.current!;
    const wts = depthWeightsRef.current;
    wts.sma10 = showSMA10 ? smaDamping(sma10Period) * 1.2 : 0;
    wts.sma20 = showSMA20 ? smaDamping(sma20Period) * 1.2 : 0;
    wts.sma50 = showSMA50 ? smaDamping(sma50Period) * 1.2 : 0;
    depth.update(U.data as any[], vis[0], vis[1], velocity, dt, wts, zLimit3D);
    if (depth.ready) {
      S.zScale = depth.zScale;
      S.mainZK = U.zScale > 0 ? depth.zScale / U.zScale : 1;
      S.wallZK = U.wallZScale > 0 ? depth.zScale / U.wallZScale : 1;
      const mz = mainZGroupsRef.current;
      for (let i = 0; i < mz.length; i++) mz[i].scale.z = S.mainZK;
      const wz = wallZGroupsRef.current;
      for (let i = 0; i < wz.length; i++) wz[i].scale.z = S.wallZK;
    }

    if (DBG_ENABLED) dbgScaleFrame(ctrl.dMin, ctrl.dMax, ctrl.vMin, ctrl.vMax, dt);
  });

  // Conectores filamento->paredes: cada linha tem uma ponta no filamento/parede (escala com o preço) e,
  // nas do chão, uma ponta fixa em floorY. Por isso a transformação vai vértice a vértice (~240 vértices),
  // só quando a escala ou os conectores mudam. Ordem por conector: filamento, parede, filamento, chão.
  const connectorsLineRef = useRef<THREE.LineSegments | null>(null);
  const connectorsXformRef = useRef({ a: NaN, c: NaN, k: NaN, src: null as Float32Array | null });
  useFrame(() => {
    const line = connectorsLineRef.current, src = uploadedRef.current?.connectorsPos, S = scaleStateRef.current;
    if (!line || !src || !S.ready) return;
    const X = connectorsXformRef.current;
    if (X.a === S.mainA && X.c === S.mainC && X.k === S.mainZK && X.src === src) return;
    const attr = line.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!attr || attr.array.length < src.length) return;
    const arr = attr.array as Float32Array;
    const n = src.length / 3;
    for (let i = 0; i < n; i++) {
      const y = src[i * 3 + 1], z = src[i * 3 + 2], kind = i & 3;
      arr[i * 3 + 1] = kind === 3 ? y : S.mainA * y + S.mainC;   // ponta do chão: Y fixo
      arr[i * 3 + 2] = kind === 1 ? z : S.mainZK * z;            // ponta da parede: Z fixo
    }
    attr.needsUpdate = true;
    X.a = S.mainA; X.c = S.mainC; X.k = S.mainZK; X.src = src;
  });

  // Domínio de preço lido pelas marcas ao vivo: preço -> Y = preço * scale + offset
  const livePriceDomain = useMemo<LiveScaleDomain>(() => ({
    read: (out: Float64Array) => {
      const S = scaleStateRef.current;
      if (!S.ready) return false;
      const k = heightScale3D / (S.dMax - S.dMin);
      out[0] = S.dMin; out[1] = S.dMax; out[2] = k; out[3] = -S.dMin * k - heightScale3D / 2;
      return true;
    },
  }), [heightScale3D]);

  // Domínio de força: momentum -> Z = momentum * zScale; marcas até a borda do cubo
  const liveForceDomain = useMemo<LiveScaleDomain>(() => ({
    read: (out: Float64Array) => {
      const S = scaleStateRef.current;
      if (!S.ready || !(S.zScale > 0)) return false;
      const maxF = zLimit3D / S.zScale;
      out[0] = -maxF; out[1] = maxF; out[2] = S.zScale; out[3] = 0;
      return true;
    },
  }), [zLimit3D]);

  /** Momentum -> Z desenhado (trackers). */
  const getForceZLive = useMemo(() => mkForceZLive(scaleStateRef, getWallForceZ), [getWallForceZ]);

  /** Preço -> Y no domínio desenhado (trackers, que calculam a posição a cada quadro). */
  const wallGetYRef = useRef(wallGetY);
  wallGetYRef.current = wallGetY;
  const getYLive = useMemo(() => mkYLive(scaleStateRef, wallGetYRef, heightScale3D), [wallGetY, heightScale3D]);

  // Parede lateral só com o trecho VISÍVEL: os pontos estão em ordem de X (posição no lote), então
  // uma busca binária acha o primeiro e o último ponto dentro da janela e setDrawRange recorta a linha.
  // A busca roda sobre a cópia que está na GPU (atributo do FastLine), nunca sobre o array do React, que o render
  // adiado do lote seguinte reescreve no lugar antes de ir para a GPU (src/utils/sideClip.ts).
  const sideWallLineRef = useRef<THREE.Line | null>(null);
  const sideSma10Ref = useRef<THREE.Line | null>(null);
  const sideSma20Ref = useRef<THREE.Line | null>(null);
  const sideSma50Ref = useRef<THREE.Line | null>(null);
  // linhas do chão: só para a sonda da lateral (?dbg=1) usar como controle
  const floorLineRef = useRef<THREE.Line | null>(null);
  const floorSma10Ref = useRef<THREE.Line | null>(null);
  const floorSma20Ref = useRef<THREE.Line | null>(null);
  const floorSma50Ref = useRef<THREE.Line | null>(null);
  const sideGpu = useRef<GpuCopy[] | null>(null);
  if (sideGpu.current === null) sideGpu.current = [newGpuCopy(), newGpuCopy(), newGpuCopy(), newGpuCopy()];
  const sideWinW = useRef(new Float64Array(2));                // janela [lo, hi] no lote da parede
  const sideWinM = useRef(new Float64Array(2));                // janela [lo, hi] no lote principal
  useFrame(() => {
    const U = uploadedRef.current;
    if (!U || !showShadowSide || !historyOffsetRef) return;
    const G = sideGpu.current!, W = sideWinW.current, WM = sideWinM.current;
    // janela da linha principal: deslocamento do lote da parede (adiado), o mesmo que está na GPU (C1)
    // (sem pontos suavizados a lateral cai no fallback e não é recortada, como antes)
    // posição relativa a cada lote (F2), como no deslocamento dos grupos
    if (U.wallSmoothedPtsCount > 1) {
      const sh = computeSliceShift(positionFor(historyOffsetRef, U.wallNewestAbs), U.wallSliceOffset, U.wallPadLeft, U.wallPadRight) * U.wallStepX;
      W[0] = startX - sh; W[1] = startX + backChartWidth - sh;
      clipToX(sideWallLineRef.current, G[0], W);
    }
    // SMAs (lote principal)
    const shM = computeSliceShift(positionFor(historyOffsetRef, U.newestAbs), U.sliceOffset, U.padLeft, U.padRight) * U.stepX;
    WM[0] = startX - shM; WM[1] = startX + backChartWidth - shM;
    if (showSMA10) clipToX(sideSma10Ref.current, G[1], WM);
    if (showSMA20) clipToX(sideSma20Ref.current, G[2], WM);
    if (showSMA50) clipToX(sideSma50Ref.current, G[3], WM);
  });

  // C1: publica o lote deste render como "na GPU". Sem lista de dependências: a cada commit da cena, DEPOIS dos
  // efeitos dos filhos (upload do FastLine / InstancedCandles) desta mesma descarga. O primeiro render já nasce
  // publicado (sem salto na montagem). O objeto é do render que foi commitado: renders descartados não publicam.
  const uploadedNow: UploadedBatch = {
    data, sliceOffset, padLeft, padRight, minP, maxP, zScale, stepX, heightScale3D,
    newestAbs: dataNewestAbs, wallNewestAbs: wallWindow.newestAbs,
    wallData, wallSliceOffset: wallWindow.sliceOffset, wallPadLeft: wallWindow.padLeft, wallPadRight: wallWindow.padRight,
    wallMinP, wallMaxP, wallZScale, wallStepX,
    wallSmoothedPoints, wallSmoothedPtsCount, sma10Pts, sma20Pts, sma50Pts, connectorsPos,
  };
  if (uploadedRef.current === null) uploadedRef.current = uploadedNow;
  useEffect(() => { uploadedRef.current = uploadedNow; });

  // Pontas contínuas da lateral: corte por X no shader, na mesma janela do recorte (src/utils/sideXClip.ts)
  useSideXClip({
    lines: [sideWallLineRef, sideSma10Ref, sideSma20Ref, sideSma50Ref],
    drawRef: uploadedRef, historyOffsetRef, startX, backChartWidth, enabled: !!showShadowSide,
  });

  // Diagnóstico (?dbg=1, P1): CPU da submissão (renderer.render) e renderer.info
  useRenderProbe();
  // Diagnóstico (?dbg=1): sonda de consistência filamento x candles (roda depois dos useFrame acima)
  useFilamentSyncProbe({
    data, wallData, sliceOffset, wallSliceOffset: wallWindow.sliceOffset, padLeft, wallPadLeft: wallWindow.padLeft,
    minP, maxP, wallMinP, wallMaxP, zScale, smoothedPoints, smoothedPtsCount, candlesBack,
    stepX, H: heightScale3D, vis: effectiveVisibleCount, historyOffsetRef, scaleStateRef, autoRotateEnabled, camera,
    filGroupX: filGroupXRef, filGroupYZ: filGroupYZRef, candGroupX: candGroupXRef, candGroupY: candGroupYRef, filLine: filLineRef,
    drawRef: uploadedRef,
  });
  // Diagnóstico (?dbg=1): sonda da lateral (e do chão, como controle), medida imediatamente antes do desenho
  useSideProbe({
    lines: [sideWallLineRef, sideSma10Ref, sideSma20Ref, sideSma50Ref, floorLineRef, floorSma10Ref, floorSma20Ref, floorSma50Ref],
    drawRef: uploadedRef, historyOffsetRef, startX, backChartWidth, H: heightScale3D,
    showSide: !!showShadowSide, showFloor: !!showShadowFloor, sma: [!!showSMA10, !!showSMA20, !!showSMA50],
    gpu: sideGpu,
  });

  /** Y do mundo -> preço, no domínio desenhado (interações com a parede). */
  const getPriceRef = useRef(getPrice);
  getPriceRef.current = getPrice;
  const getPriceLive = useMemo(() => mkPriceLive(scaleStateRef, getPriceRef, heightScale3D), [getPrice, heightScale3D]);


  return (
    <>
      <OrbitControls 
        ref={controlsRef}
        enabled={(interactionMode === 'orbit' || isReturningToLive) && !cameraTweenTarget && !isRadialMenuOpen}
        makeDefault
        enableDamping={true}
        dampingFactor={0.05}
        rotateSpeed={controlRotateSpeed}
        enablePan={true}
        panSpeed={controlPanSpeed}
        zoomSpeed={controlZoomSpeed}
        minPolarAngle={0}
        maxPolarAngle={Math.PI}
        autoRotate={autoRotateEnabled}
        autoRotateSpeed={0.2} 
        minDistance={50} 
        maxDistance={25000} 
        onStart={() => {
          if (cameraTweenTarget) return;
          
          handleUserInteraction();
        }}
        onEnd={() => {
          
          if (camera && controlsRef.current) {
            saveCameraState(timeframe, 
              [camera.position.x, camera.position.y, camera.position.z],
              [controlsRef.current.target.x, controlsRef.current.target.y, controlsRef.current.target.z]
            );
          }
        }}
      />
      
      {data && data.length > 0 && (
      <group>
        {/* Dedicated Back Wall Exact Mesh for Long Press */}
        <mesh
          raycast={interactionMode !== 'orbit' ? () => {} : THREE.Mesh.prototype.raycast}
          position={[startX + width / 2, (bottomOfGrid + topOfGrid) / 2, wallZ + 0.5]}
          onPointerDown={(e) => {
            if (e.object !== e.eventObject) return;
            if (interactionMode === 'orbit') {
              const activeGesture = useFlowaesStore.getState()?.activeGesture;
              if (activeGesture && activeGesture !== 'long_press_back') {
                return;
              }
              longPressBackWallStartRef.current = { x: e.clientX, y: e.clientY };
              if (pressIntentTimerRef.current) clearTimeout(pressIntentTimerRef.current);
              pressIntentTimerRef.current = setTimeout(() => {
                setActivePressPlane('back');
                pressIntentTimerRef.current = null;
              }, 150);
            }
          }}
          onPointerMove={(e) => {
            const dist = Math.hypot(e.clientX - longPressBackWallStartRef.current.x, e.clientY - longPressBackWallStartRef.current.y);
            if (dist > 30) {
              if (pressIntentTimerRef.current) {
                clearTimeout(pressIntentTimerRef.current);
                pressIntentTimerRef.current = null;
              }
              if (activePressPlane === 'back') {
                setActivePressPlane(null);
              }
            }
          }}
          onPointerUp={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
          onPointerOut={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
        >
          <planeGeometry args={[width, topOfGrid - bottomOfGrid]} />
          <meshBasicMaterial toneMapped={false} visible={false} />
        </mesh>

        {/* Dedicated Floor Exact Mesh for Long Press */}
        <mesh 
          raycast={interactionMode !== 'orbit' ? () => {} : THREE.Mesh.prototype.raycast}
          position={[startX + width / 2, floorY + 0.5, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          onPointerDown={(e) => {
            if (e.object !== e.eventObject) return;
            if (interactionMode === 'orbit') {
              const activeGesture = useFlowaesStore.getState()?.activeGesture;
              if (activeGesture && activeGesture !== 'long_press_floor') return;
              
              longPressFloorStartRef.current = { x: e.clientX, y: e.clientY };
              if (pressIntentTimerRef.current) clearTimeout(pressIntentTimerRef.current);
              pressIntentTimerRef.current = setTimeout(() => {
                setActivePressPlane('floor');
                pressIntentTimerRef.current = null;
              }, 150);
            }
          }}
          onPointerMove={(e) => {
            const dist = Math.hypot(e.clientX - longPressFloorStartRef.current.x, e.clientY - longPressFloorStartRef.current.y);
            if (dist > 30) {
              if (pressIntentTimerRef.current) {
                clearTimeout(pressIntentTimerRef.current);
                pressIntentTimerRef.current = null;
              }
              if (activePressPlane === 'floor') {
                setActivePressPlane(null);
              }
            }
          }}
          onPointerUp={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
          onPointerOut={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
        >
          <planeGeometry args={[width, 2 * zLimit]} />
          <meshBasicMaterial toneMapped={false} visible={false} />
        </mesh>

        {/* Dedicated Side Wall Exact Mesh for Long Press */}
        <mesh 
          raycast={interactionMode !== 'orbit' ? () => {} : THREE.Mesh.prototype.raycast}
          position={[sideX + 0.5, (bottomOfGrid + topOfGrid) / 2, 0]}
          rotation={[0, Math.PI / 2, 0]}
          onPointerDown={(e) => {
            if (e.object !== e.eventObject) return;
            if (interactionMode === 'orbit') {
              const activeGesture = useFlowaesStore.getState()?.activeGesture;
              if (activeGesture && activeGesture !== 'long_press_side') return;
              
              longPressSideStartRef.current = { x: e.clientX, y: e.clientY };
              if (pressIntentTimerRef.current) clearTimeout(pressIntentTimerRef.current);
              pressIntentTimerRef.current = setTimeout(() => {
                setActivePressPlane('side');
                pressIntentTimerRef.current = null;
              }, 150);
            }
          }}
          onPointerMove={(e) => {
            const dist = Math.hypot(e.clientX - longPressSideStartRef.current.x, e.clientY - longPressSideStartRef.current.y);
            if (dist > 30) {
              if (pressIntentTimerRef.current) {
                clearTimeout(pressIntentTimerRef.current);
                pressIntentTimerRef.current = null;
              }
              if (activePressPlane === 'side') {
                setActivePressPlane(null);
              }
            }
          }}
          onPointerUp={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
          onPointerOut={() => {
            if (pressIntentTimerRef.current) { clearTimeout(pressIntentTimerRef.current); pressIntentTimerRef.current = null; }
            setActivePressPlane(null);
          }}
        >
          <planeGeometry args={[2 * zLimit, topOfGrid - bottomOfGrid]} />
          <meshBasicMaterial toneMapped={false} visible={false} />
        </mesh>

        
        {/* Filament Hold-to-Time-Travel Area */}
        <mesh 
          position={[startX + width / 2, 0, 0]}
          onPointerDown={(e) => {
            if (interactionMode === 'orbit') {
              if (pressIntentTimerRef.current) clearTimeout(pressIntentTimerRef.current);
              
              // 1) O "Filtro de Toque Limpo" (Single-Tap Hold)
              const now = performance.now();
              const timeSinceLastTap = now - lastTapTimeRef.current;
              
              if (timeSinceLastTap < 400) {
                 // É um múltiplo clique (duplo ou triplo)
                 clickCountRef.current += 1;
              } else {
                 // É um toque limpo/isolado
                 clickCountRef.current = 1;
              }
              lastTapTimeRef.current = now;

              // Se for mais de 1 clique seguido, ABORTA a Máquina do Tempo
              if (clickCountRef.current > 1) {
                 return; 
              }

              longPressMidWallStartRef.current = { x: e.clientX, y: e.clientY };
              longPressPointerIdRef.current = e.pointerId; // P2a: o traço armado pelo toque longo é deste dedo
              
              pressIntentTimerRef.current = setTimeout(() => {
                const isMenuOpen = isRadialMenuOpen;
                if (isMenuOpen) return;
                // F3b C3 (árbitro): não arma com a roleta abrindo/aberta nem com troca de série em andamento
                if (useFlowaesStore.getState().activeGesture === 'radial_menu' || histRuntime.switching) { pressIntentTimerRef.current = null; return; }

                cancelTimeModeIdleTimer();
                if (typeof setInteractionMode === 'function') {
                  setInteractionMode('time');
                }
                if (typeof setIsReturningToLive === 'function') {
                  setIsReturningToLive(false);
                }
                
                // Haptic feedback
                if (typeof navigator !== 'undefined' && navigator.vibrate) {
                  navigator.vibrate(50);
                }

                useFlowaesStore.getState().setActiveGesture('time_drag');
                strokeBegin(timeDragStroke, longPressPointerIdRef.current, longPressMidWallStartRef.current.x, performance.now());
                timeDragVelocityRef.current = 0;
                timeDragLastTimeRef.current = performance.now();
                pressIntentTimerRef.current = null;
              }, 1200);
            } else if (interactionMode === 'time' && !isReturningToLive) {
              const activeGesture = useFlowaesStore.getState()?.activeGesture;
              if (activeGesture && activeGesture !== 'time_drag') return;
              e.stopPropagation();
              cancelTimeModeIdleTimer();
              useFlowaesStore.getState().setActiveGesture('time_drag');
              // P2a: referência fixada aqui, síncrona; um segundo dedo não rouba o traço do primeiro
              if (strokeMayBegin(timeDragStroke, e.pointerId, performance.now())) {
                strokeBegin(timeDragStroke, e.pointerId, e.clientX, performance.now());
                timeDragVelocityRef.current = 0;
                timeDragLastTimeRef.current = performance.now();
              }
            }
          }}
          onPointerMove={(e) => {
            if (interactionMode === 'orbit' && pressIntentTimerRef.current) {
              const dist = Math.hypot(e.clientX - longPressMidWallStartRef.current.x, e.clientY - longPressMidWallStartRef.current.y);
              if (dist > 40) {
                clearTimeout(pressIntentTimerRef.current);
                pressIntentTimerRef.current = null;
              }
            } else if (interactionMode === 'time' && !isReturningToLive) {
               // F3b C3 (árbitro): o toque duplo + segurar da roleta não arrasta o tempo enquanto espera abrir
               if (strokeOwns(timeDragStroke, e.pointerId) && useFlowaesStore.getState().activeGesture !== 'radial_menu') {
                 e.stopPropagation();
                 cancelTimeModeIdleTimer();
                 // Só grava quando muda: set() sem mudança notifica a store inteira e re-renderiza o App a cada toque
                 if (useFlowaesStore.getState().activeGesture !== 'time_drag') useFlowaesStore.getState().setActiveGesture('time_drag');
                 const refBeforeP1c = DBG_ENABLED ? timeDragPointerXRef.current : null; // P1c: só anota
                 // P2a: só o dedo do traço; referência vazia/inválida dá 0 e passa a ser o x atual (nunca X − 0)
                 const movement = strokeStep(timeDragStroke, e.pointerId, e.clientX, performance.now());
                 const sensitivity = 1.00;
                 // Ganho constante em todo o histórico; a parada nos extremos é a trava abaixo (seca, sem fricção).
                 const offsetMovement = movement * sensitivity;
                 // F0: o alvo nunca passa das velas já carregadas (com ou sem fim marcado): sem dívida, sem salto quando o bloco chega
                 applyTimeDrag(offsetMovement);
                 if (DBG_ENABLED) touchDrag(e.clientX, (e as any).pointerId, refBeforeP1c, timeDragPointerXRef.current, offsetMovement, useFlowaesStore.getState().activeGesture);
               }
            }
          }}
          onPointerUp={(e) => {
            if (pressIntentTimerRef.current) {
              clearTimeout(pressIntentTimerRef.current);
              pressIntentTimerRef.current = null;
            }
            if (interactionMode === 'time' && strokeEnd(timeDragStroke, e.pointerId)) { // P2a: só o up do dedo do traço
               useFlowaesStore.getState().setActiveGesture(null);
               startTimeModeIdleTimer();
            }
          }}
          onPointerLeave={(e) => {
            if (pressIntentTimerRef.current) {
              clearTimeout(pressIntentTimerRef.current);
              pressIntentTimerRef.current = null;
            }
            // P2a: troca de hover para o outro mesh com o dedo na tela (o r3f chama o leave com o que está sob o dedo)
            // não encerra o traço; sair de tudo (canvas, cancelamento: interseções vazias) encerra, como antes
            if (interactionMode === 'time' && !isHoverSwap(e)) {
               strokeEnd(timeDragStroke);
               if (useFlowaesStore.getState().activeGesture === 'time_drag') {
                 useFlowaesStore.getState().setActiveGesture(null);
               }
               startTimeModeIdleTimer();
            }
          }}
        >
          <boxGeometry args={[width, heightScale * 1.2, frontZ * 1.5]} />
          <meshBasicMaterial toneMapped={false} visible={false} />
        </mesh>

{/* Broad interaction plane for crosshair/time drag/zone creation */}
        <mesh 
          position={[0, 0, wallZ]} 
          raycast={interactionMode === 'orbit' ? () => {} : THREE.Mesh.prototype.raycast}
          onPointerDown={(e) => {
            if (e.object !== e.eventObject) return;

            if (interactionMode === 'crosshair') {
              e.stopPropagation();
              if (!isCrosshairPinned) {
                setIsCrosshairActive(true);
                if(crosshairRef.current) crosshairRef.current.setIndex(calcIndex(e.point.x));
              }
            } else if (interactionMode === 'time' && !isReturningToLive) {
              const activeGesture = useFlowaesStore.getState()?.activeGesture;
              if (activeGesture && activeGesture !== 'time_drag') return;
              e.stopPropagation();
              cancelTimeModeIdleTimer();
              useFlowaesStore.getState().setActiveGesture('time_drag');
              // P2a: referência fixada aqui, síncrona; um segundo dedo não rouba o traço do primeiro
              if (strokeMayBegin(timeDragStroke, e.pointerId, performance.now())) {
                strokeBegin(timeDragStroke, e.pointerId, e.clientX, performance.now());
                timeDragVelocityRef.current = 0;
                timeDragLastTimeRef.current = performance.now();
              }
            } else if (interactionMode === 'zone') {
              if (selectedZoneId) {
                if (typeof setSelectedZoneId === 'function') setSelectedZoneId(null);
                if (typeof setInteractionMode === 'function') setInteractionMode('orbit');
                return; // Do not stop propagation, allow OrbitControls to catch the drag
              }
              e.stopPropagation();
              const price = getPriceLive(e.point.y);
              const time = getTime(e.point.x);
              setDrawingZone({
                id: crypto.randomUUID(),
                priceStart: price,
                priceEnd: price,
                timeStart: time,
                timeEnd: time,
                depthStart: 0,
                depthEnd: 1,
                color: '#2a2a2a',
                opacity: 0.5
              });
            }
          }}
          onPointerMove={(e) => {
            if (interactionMode === 'crosshair') {
               // Crosshair now handles its own movement via useFrame (Performance Optimization)
            } else if (interactionMode === 'time' && !isReturningToLive) {
               // F3b C3 (árbitro): o toque duplo + segurar da roleta não arrasta o tempo enquanto espera abrir
               if (strokeOwns(timeDragStroke, e.pointerId) && useFlowaesStore.getState().activeGesture !== 'radial_menu') {
                 e.stopPropagation();
                 cancelTimeModeIdleTimer();
                 // Só grava quando muda: set() sem mudança notifica a store inteira e re-renderiza o App a cada toque
                 if (useFlowaesStore.getState().activeGesture !== 'time_drag') useFlowaesStore.getState().setActiveGesture('time_drag');
                 const now = performance.now();
                 const dt = Math.max(1, now - timeDragLastTimeRef.current);
                 const refBeforeP1c = DBG_ENABLED ? timeDragPointerXRef.current : null; // P1c: só anota
                 // P2a: só o dedo do traço; referência vazia/inválida dá 0 e passa a ser o x atual (nunca X − 0)
                 const movement = strokeStep(timeDragStroke, e.pointerId, e.clientX, performance.now());
                 const sensitivity = 1.00;
                 // Ganho constante em todo o histórico; a parada nos extremos é a trava abaixo (seca, sem fricção).
                 const offsetMovement = movement * sensitivity;
                 // F0: o alvo nunca passa das velas já carregadas (com ou sem fim marcado): sem dívida, sem salto quando o bloco chega
                 applyTimeDrag(offsetMovement);
                 if (DBG_ENABLED) touchDrag(e.clientX, (e as any).pointerId, refBeforeP1c, timeDragPointerXRef.current, offsetMovement, useFlowaesStore.getState().activeGesture);
                 
                 // Exponential moving average for smoother velocity
                 const currentVel = offsetMovement / dt;
                 timeDragVelocityRef.current = timeDragVelocityRef.current * 0.5 + currentVel * 0.5;
                 timeDragLastTimeRef.current = now;
               }
            } else if (interactionMode === 'zone') {
               if (dragState) {
                 e.stopPropagation();
                 const curPrice = getPriceLive(e.point.y);
                 const curTime = getTime(e.point.x);
                 
                 const deltaPrice = curPrice - dragState.startPrice;
                 const deltaTime = curTime - dragState.startTime;
                 const deltaDepth = (curTime - dragState.startTime) * 0.5 + (curPrice - dragState.startPrice) * 0.5; // Heuristic for depth drag if needed, or we use curDepth if hit plane was at box Z

                 if (typeof setZones === 'function') {
                   setZones((prev: PriceZone[]) => prev.map(z => {
                     if (z.id === dragState.zoneId) {
                        let newPriceStart = dragState.initialPriceStart;
                        let newPriceEnd = dragState.initialPriceEnd;
                        let newTimeStart = dragState.initialTimeStart;
                        let newTimeEnd = dragState.initialTimeEnd;
                        let newDepthStart = dragState.initialDepthStart;
                        let newDepthEnd = dragState.initialDepthEnd;

                        if (dragState.handle === '') {
                          const w = dragState.initialTimeEnd - dragState.initialTimeStart;
                          const h = dragState.initialPriceEnd - dragState.initialPriceStart;
                          
                          newTimeStart = dragState.initialTimeStart + deltaTime;
                          newTimeEnd = newTimeStart + w;
                          
                          newPriceStart = dragState.initialPriceStart + deltaPrice;
                          newPriceEnd = newPriceStart + h;
                          
                          newDepthStart = dragState.initialDepthStart;
                          newDepthEnd = dragState.initialDepthEnd;
                          
                          // Clamp time
                          if (newTimeStart < 0) { newTimeStart = 0; newTimeEnd = w; }
                          if (newTimeEnd > 1) { newTimeEnd = 1; newTimeStart = 1 - w; }
                          
                          // Clamp price
                          const mPStart = Math.min(newPriceStart, newPriceEnd);
                          
                          if (mPStart < minP) {
                            const offset = minP - mPStart;
                            newPriceStart += offset;
                            newPriceEnd += offset;
                          }
                          if (Math.max(newPriceStart, newPriceEnd) > maxP) {
                            const offset = maxP - Math.max(newPriceStart, newPriceEnd);
                            newPriceStart += offset;
                            newPriceEnd += offset;
                          }
                        } else if (dragState.handle === 'left') {
                          newTimeStart = Math.max(0, Math.min(1, newTimeStart + deltaTime));
                        } else if (dragState.handle === 'right') {
                          newTimeEnd = Math.max(0, Math.min(1, newTimeEnd + deltaTime));
                        } else if (dragState.handle === 'top') {
                          newPriceEnd = Math.max(minP, Math.min(maxP, newPriceEnd + deltaPrice));
                        } else if (dragState.handle === 'bottom') {
                          newPriceStart = Math.max(minP, Math.min(maxP, newPriceStart + deltaPrice));
                        } else if (dragState.handle === 'front') {
                          newDepthEnd = Math.max(0, Math.min(1, newDepthEnd + deltaDepth));
                        } else if (dragState.handle === 'back') {
                          newDepthStart = Math.max(0, Math.min(1, newDepthStart + deltaDepth));
                        }

                       return {
                          ...z,
                          priceStart: newPriceStart,
                          priceEnd: newPriceEnd,
                          timeStart: newTimeStart,
                          timeEnd: newTimeEnd,
                          depthStart: newDepthStart,
                          depthEnd: newDepthEnd
                       };
                     }
                     return z;
                   }));
                 }
                 // NO LONGER update dragState every frame to maintain relative-to-start precision
               } else if (drawingZone) {
                 e.stopPropagation();
                 setDrawingZone(prev => {
                   if (!prev) return null;
                   return { ...prev, priceEnd: getPriceLive(e.point.y), timeEnd: getTime(e.point.x) };
                 });
               }
            }
          }}
          onPointerUp={(e) => {
            if (interactionMode === 'time' && strokeEnd(timeDragStroke, e.pointerId)) { // P2a: só o up do dedo do traço
              if (useFlowaesStore.getState().activeGesture === 'time_drag') {
                useFlowaesStore.getState().setActiveGesture(null);
              }
              startTimeModeIdleTimer();
            }
            if (dragState) {
              e.stopPropagation();
              if (typeof setZones === 'function') {
                setZones((prev: PriceZone[]) => prev.map(z => {
                  if (z.id === dragState.zoneId) {
                    return {
                      ...z,
                      priceStart: Math.min(z.priceStart, z.priceEnd),
                      priceEnd: Math.max(z.priceStart, z.priceEnd),
                      timeStart: Math.min(z.timeStart, z.timeEnd),
                      timeEnd: Math.max(z.timeStart, z.timeEnd),
                      depthStart: Math.min(z.depthStart ?? 0, z.depthEnd ?? 1),
                      depthEnd: Math.max(z.depthStart ?? 0, z.depthEnd ?? 1)
                    };
                  }
                  return z;
                }));
              }
              setDragState(null);
            } else if (interactionMode === 'zone' && drawingZone) {
              e.stopPropagation();
              if (drawingZone.priceStart !== undefined && drawingZone.timeStart !== undefined) {
                const z = { ...drawingZone, priceEnd: getPriceLive(e.point.y), timeEnd: getTime(e.point.x) } as PriceZone;
                // Normalize min/max
                const finalZ: PriceZone = {
                  ...z,
                  priceStart: Math.min(z.priceStart, z.priceEnd),
                  priceEnd: Math.max(z.priceStart, z.priceEnd),
                  timeStart: Math.min(z.timeStart, z.timeEnd),
                  timeEnd: Math.max(z.timeStart, z.timeEnd),
                  depthStart: Math.min(z.depthStart ?? 0, z.depthEnd ?? 1),
                  depthEnd: Math.max(z.depthStart ?? 0, z.depthEnd ?? 1)
                };
                // Require a minimum size to avoid accidental clicks registering as zones
                const widthUnits = Math.abs(finalZ.timeEnd - finalZ.timeStart);
                const heightUnits = Math.abs(finalZ.priceEnd - finalZ.priceStart);

                if (widthUnits > 0.001 && heightUnits > 0.001) {
                  if (typeof setZones === 'function') setZones((prev: PriceZone[]) => {
                    if (prev.some(z => z.id === finalZ.id)) return prev;
                    return [...prev, finalZ];
                  });
                  if (typeof setSelectedZoneId === 'function') setSelectedZoneId(finalZ.id);
                } else {
                  // Clicked outside a zone without dragging: Deselect and switch to Orbit
                  if (typeof setSelectedZoneId === 'function') setSelectedZoneId(null);
                  if (typeof setInteractionMode === 'function') setInteractionMode('orbit');
                }
              }
              setDrawingZone(null);
            }
          }}
          onPointerOut={(e) => {
            // P2a: o r3f chama este out DENTRO do e.stopPropagation() da área do filamento quando o dedo entra nela
            // (troca de hover); zerar a referência aí dava X − null = X velas. Troca de hover não encerra o traço.
            if (interactionMode === 'time' && !isHoverSwap(e)) {
              strokeEnd(timeDragStroke);
              if (useFlowaesStore.getState().activeGesture === 'time_drag') {
                useFlowaesStore.getState().setActiveGesture(null);
              }
              startTimeModeIdleTimer();
            }
            if (dragState) setDragState(null);
            if (drawingZone) setDrawingZone(null);
          }}
        >
          <planeGeometry args={[20000, 20000]} />
          <meshBasicMaterial toneMapped={false} visible={false} />
        </mesh>

        <CrosshairRenderer ref={crosshairRef} interactionMode={interactionMode} width={bufferWidth} currentPoints3D={currentPoints3D} xformRef={scaleStateRef} data={data} floorY={floorY} topY={topY} startX={bufferStartX} axisX={axisX} wallZ={wallZ} frontZ={frontZ} />

        {show3D && smoothedPtsCount > 0 && (
          <group ref={addFilGroupX}>
          <group ref={addFilGroupYZ}>
            {/* 
              TODO: Risco de expansão por momentum no filamento central 3D.
              - Comportamento observado/esperado: Em momentos de picos extremos de volatilidade e aceleração
                de momentum, o filamento tridimensional pode expandir além das bordas volumétricas do cubo
                nos eixos Z (força) e Y (oscilação IMF de preço), o que resulta em um corte reto no espaço vazio.
              - Decisão técnica adiada: Optou-se intencionalmente por aplicar o clipping volumétrico estrito
                reaproveitando os mesmos limites das paredes nesta fase. Decisões sobre tratamento especial
                (como recuo automático de câmera, fade out gradual nas bordas ou destaque visual de saturação)
                foram propositalmente postergadas para serem revisitadas em etapa futura.
            */}
            {/* Back half of the main 3D line */}
            <FastLine
              ref={filLineRef}
              points={smoothedPoints} 
              vertexColors={smoothedColors3D} 
              lineWidth={lineWidth3D} 
              transparent={true}
              opacity={main3DOpacity}
              depthWrite={true}
              depthTest={true}
              frustumCulled={false}
              renderOrder={0}
              clippingPlanes={central3DClippingPlanes}
            />
            {/* Front half of the main 3D line */}
            <FastLine
              points={smoothedPoints} 
              vertexColors={smoothedColors3D} 
              lineWidth={lineWidth3D} 
              transparent={true}
              opacity={main3DOpacity}
              depthWrite={true}
              depthTest={true}
              frustumCulled={false}
              renderOrder={2}
              clippingPlanes={central3DClippingPlanes}
            />

            {/* SMA 10 Line in spatial 3D (following the wave trend) */}
            {showSMA10 && sma10Pts.length > 1 && (
              <>
                <FastLine
                  points={sma10Pts} 
                  vertexColors={sma10Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma103DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={0}
                  clippingPlanes={central3DClippingPlanes}
                />
                <FastLine
                  points={sma10Pts} 
                  vertexColors={sma10Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma103DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={2}
                  clippingPlanes={central3DClippingPlanes}
                />
              </>
            )}
            {/* SMA 20 Line in spatial 3D */}
            {showSMA20 && sma20Pts.length > 1 && (
              <>
                <FastLine
                  points={sma20Pts} 
                  vertexColors={sma20Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma203DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={0}
                  clippingPlanes={central3DClippingPlanes}
                />
                <FastLine
                  points={sma20Pts} 
                  vertexColors={sma20Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma203DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={2}
                  clippingPlanes={central3DClippingPlanes}
                />
              </>
            )}
            {/* SMA 50 Line in spatial 3D */}
            {showSMA50 && sma50Pts.length > 1 && (
              <>
                <FastLine
                  points={sma50Pts} 
                  vertexColors={sma50Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma503DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={0}
                  clippingPlanes={central3DClippingPlanes}
                />
                <FastLine
                  points={sma50Pts} 
                  vertexColors={sma50Cols} 
                  lineWidth={3} 
                  transparent={true}
                  opacity={sma503DOpacity}
                  depthWrite={true}
                  depthTest={true}
                  frustumCulled={false}
                  renderOrder={2}
                  clippingPlanes={central3DClippingPlanes}
                />
              </>
            )}
          </group>
          </group>
        )}

        {/* Zones Rendering (geometria no domínio-base do lote principal) */}
        <group ref={addToMainYGroup}>
        {[...(zones || []), ...((drawingZone && drawingZone.priceStart !== undefined && drawingZone.timeStart !== undefined && drawingZone.priceEnd !== undefined && drawingZone.timeEnd !== undefined) ? [{ ...drawingZone, isDrawing: true } as any] : [])].map((z) => {
          const y1 = getY(z.priceStart);
          const y2 = getY(z.priceEnd);
          const x1 = startX + z.timeStart * width;
          const x2 = startX + z.timeEnd * width;
          const z1 = wallZ + (z.depthStart ?? 0) * (frontZ - wallZ);
          const z2 = wallZ + (z.depthEnd ?? 1) * (frontZ - wallZ);
          const xMin = Math.min(x1, x2);
          const xMax = Math.max(x1, x2);
          const yMin = Math.min(y1, y2);
          const yMax = Math.max(y1, y2);
          const zMin = Math.min(z1, z2);
          const zMax = Math.max(z1, z2);
          const widthX = Math.max(0.01, xMax - xMin);
          const heightY = Math.max(0.01, yMax - yMin);
          const depthZ = Math.max(0.01, zMax - zMin);
          const xCenter = (xMin + xMax) / 2;
          const yCenter = (yMin + yMax) / 2;
          const zCenter = (zMin + zMax) / 2;
          const isSelected = selectedZoneId === z.id;

          return (
            <group key={z.isDrawing ? 'drawing-zone' : `zone-${z.id}`}>
              <mesh 
                position={[xCenter, yCenter, zCenter]}
                raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                onPointerDown={(e) => {
                  if (!z.isDrawing && !z.locked) {
                    if (e.object === e.eventObject) e.stopPropagation();
                    if (interactionMode !== 'zone') {
                      if (typeof setInteractionMode === 'function') setInteractionMode('zone');
                    }
                    if (typeof setSelectedZoneId === 'function') setSelectedZoneId(z.id);
                  }
                }}
              >
                <boxGeometry args={[widthX, heightY, depthZ]} />
                <meshBasicMaterial toneMapped={false} 
                  color={z.color || '#00d9ff'} 
                  transparent depthWrite={false} 
                  opacity={isSelected ? 0.5 : (z.opacity || 0.2)} 
                  
                  side={THREE.DoubleSide}
                />
              </mesh>
              {isSelected && !z.isDrawing && !z.locked && (
                <>
                  {/* Smart Handles */}
                  <mesh position={[xMin, yCenter, zCenter]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'left' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'left' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xMax, yCenter, zCenter]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'right' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'right' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xCenter, yMax, zCenter]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'top' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'top' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xCenter, yMin, zCenter]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'bottom' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'bottom' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xCenter, yCenter, zMax]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'front' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'front' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xCenter, yCenter, zMin]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: 'back' 
                      }); 
                  }}>
                    <sphereGeometry args={[20, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === 'back' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  <mesh position={[xCenter, yCenter, zCenter]} 
                    raycast={dragState ? () => {} : THREE.Mesh.prototype.raycast}
                    onPointerDown={(e) => { 
                      e.stopPropagation(); 
                      if (interactionMode !== 'zone') setInteractionMode('zone');
                      setDragState({ 
                        zoneId: z.id, 
                        startPrice: getPriceLive(e.point.y), 
                        startTime: getTime(e.point.x), 
                        startDepth: getDepth(e.point.z),
                        initialPriceStart: z.priceStart,
                        initialPriceEnd: z.priceEnd,
                        initialTimeStart: z.timeStart,
                        initialTimeEnd: z.timeEnd,
                        initialDepthStart: z.depthStart ?? 0,
                        initialDepthEnd: z.depthEnd ?? 1,
                        handle: '' 
                      }); 
                  }}>
                    <sphereGeometry args={[28, 16, 16]} />
                    <meshBasicMaterial toneMapped={false} color={dragState?.handle === '' ? "#00ff88" : "#ffffff"} transparent depthWrite={false} depthTest={false} opacity={0.9} />
                  </mesh>
                  {/* Internal Lines (12 levels) */}
                  {[...Array(12)].map((_, i) => {
                    const lineY = yMin + (yMax - yMin) * ((i + 1) / 13);
                    return (
                      <group key={`internal-line-${i}`}>
                        <FastLine points={[xMin, lineY, zMin, xMax, lineY, zMin]} 
                          color="#888888" 
                          lineWidth={1} 
                          opacity={0.3} 
                          transparent 
                          
                        depthWrite={false} />
                        <FastLine points={[xMin, lineY, zMax, xMax, lineY, zMax]} 
                          color="#888888" 
                          lineWidth={1} 
                          opacity={0.3} 
                          transparent 
                          
                        depthWrite={false} />
                      </group>
                    );
                  })}
                </>
              )}
              <group visible={isSelected && !z.isDrawing}>
                <Html position={[xCenter, yMax + 40, zCenter]}  zIndexRange={[100, 0]} style={{ display: (isSelected && !z.isDrawing) ? 'block' : 'none', pointerEvents: (isSelected && !z.isDrawing) ? 'auto' : 'none' }}>
                  <div className="bg-[#0a0e27]/95 backdrop-blur-xl border border-[#00d9ff]/30 rounded-xl p-2 flex flex-col gap-2 pointer-events-auto shadow-2xl min-w-[40px]">
                    <div className="flex gap-2">
                      {z.locked ? (
                        <HoldToUnlock onUnlock={() => typeof setZones === 'function' && setZones((prev: PriceZone[]) => prev.map(pz => pz.id === z.id ? { ...pz, locked: false } : pz))} />
                      ) : (
                        <>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (typeof setZones === 'function') setZones((prev: PriceZone[]) => prev.map(pz => pz.id === z.id ? { ...pz, locked: true } : pz));
                            }}
                            className="p-2 rounded-lg hover:bg-[#00d9ff]/20 text-[#00d9ff] transition-all flex items-center justify-center"
                            title="Bloquear Zona"
                          >
                            <Lock size={14} />
                          </button>
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              if (typeof setZones === 'function') setZones((prev: PriceZone[]) => prev.filter(pz => pz.id !== z.id)); 
                              if (typeof setSelectedZoneId === 'function') setSelectedZoneId(null); 
                            }}
                            className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all flex items-center justify-center"
                            title="Remover Zona"
                          >
                            <Trash2 size={14} />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowZoneSettingsId(showZoneSettingsId === z.id ? null : z.id);
                            }}
                            className={cn(
                              "p-2 rounded-lg transition-all flex items-center justify-center",
                              showZoneSettingsId === z.id ? "bg-[#00d9ff] text-[#0a0e27]" : "hover:bg-[#00d9ff]/20 text-[#00d9ff]"
                            )}
                            title="Configurações da Zona"
                          >
                            <MoreHorizontal size={14} />
                          </button>
                        </>
                      )}
                    </div>

                    {showZoneSettingsId === z.id && !z.locked && (
                      <div className="flex flex-col gap-3 p-2 border-t border-white/10 mt-1 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="space-y-2">
                          <label className="text-[9px] uppercase tracking-wider text-white/50 font-bold flex items-center gap-1">
                            <Palette size={10} /> Cor
                          </label>
                          <div className="grid grid-cols-5 gap-1.5">
                            {['#2a2a2a', '#ef4444', '#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#ffffff'].map(c => (
                              <button
                                key={c}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (typeof setZones === 'function') setZones((prev: PriceZone[]) => prev.map(pz => pz.id === z.id ? { ...pz, color: c } : pz));
                                }}
                                className={cn(
                                  "w-5 h-5 rounded-md border transition-all",
                                  z.color === c ? "border-white scale-110 shadow-lg" : "border-transparent hover:scale-105"
                                )}
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <label className="text-[9px] uppercase tracking-wider text-white/50 font-bold flex items-center gap-1">
                            <Activity size={10} /> Opacidade ({(z.opacity * 100).toFixed(0)}%)
                          </label>
                          <input 
                            type="range"
                            min="0.05"
                            max="1"
                            step="0.05"
                            value={z.opacity}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (typeof setZones === 'function') setZones((prev: PriceZone[]) => prev.map(pz => pz.id === z.id ? { ...pz, opacity: val } : pz));
                            }}
                            className="w-full accent-[#00d9ff] h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Html>
              </group>
            </group>
          );
        })}
        </group>

        <>
          {/* Price vs Time Projection (Back wall) */}
          <group ref={addCandGroupX}>
          <group ref={addCandGroupY}>
            {backwallStyle === 'line' ? (
            <FastLine 
                dynamicData={{
                  data: wallData,
                  newestAbs: wallWindow.newestAbs,
                  historyOffsetRef,
                  windowSize: wallData.length,
                  extractor: (d: any, i: number, maxI: number) => [wallBufferStartX + (i / Math.max(1, maxI)) * wallBufferWidth, wallGetY(d.price), wallZ]
                }}
                color="#ffffff" lineWidth={1.5} opacity={backChartOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={backChartClippingPlanes} />
          ) : (
            <InstancedCandles 
              candles={candlesBack}
              width={wallBufferWidth}
              startX={wallBufferStartX}
              wallZ={wallZ}
              minP={wallMinP}
              maxP={wallMaxP}
              heightScale3D={heightScale3D}
              opacity={backChartOpacity}
              clippingPlanes={backChartClippingPlanes}
            />
          )}

            {/* SMA Lines on Back Wall */}
            {showSMA10 && (
              <FastLine 
                dynamicData={{
                  data: wallData,
                  newestAbs: wallWindow.newestAbs,
                  historyOffsetRef,
                  windowSize: wallData.length,
                  extractor: (d: any, i: number, maxI: number) => [wallBufferStartX + (i / Math.max(1, maxI)) * wallBufferWidth, wallGetY(d.sma10 ?? d.close ?? d.price), wallZ]
                }}
                color="#facc15" lineWidth={backSmaLineWidth} opacity={backSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} 
                clippingPlanes={backChartClippingPlanes}
              />
            )}
            {showSMA20 && (
              <FastLine 
                dynamicData={{
                  data: wallData,
                  newestAbs: wallWindow.newestAbs,
                  historyOffsetRef,
                  windowSize: wallData.length,
                  extractor: (d: any, i: number, maxI: number) => [wallBufferStartX + (i / Math.max(1, maxI)) * wallBufferWidth, wallGetY(d.sma20 ?? d.close ?? d.price), wallZ]
                }}
                color="#38bdf8" lineWidth={backSmaLineWidth} opacity={backSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} 
                clippingPlanes={backChartClippingPlanes}
              />
            )}
            {showSMA50 && (
              <FastLine 
                dynamicData={{
                  data: wallData,
                  newestAbs: wallWindow.newestAbs,
                  historyOffsetRef,
                  windowSize: wallData.length,
                  extractor: (d: any, i: number, maxI: number) => [wallBufferStartX + (i / Math.max(1, maxI)) * wallBufferWidth, wallGetY(d.sma50 ?? d.close ?? d.price), wallZ]
                }}
                color="#a855f7" lineWidth={backSmaLineWidth} opacity={backSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} 
                clippingPlanes={backChartClippingPlanes}
              />
            )}
          </group>
          </group>

            {/* Unified 2D Canvas Texture Grid for Back Wall */}
            <BackWallGridTexture 
              data={wallDataVisible}
              drawPriceLines={false}
              timeframe={timeframe}
              minP={wallMinP}
              maxP={wallMaxP}
              getY={wallGetY}
              startX={startX}
              width={width}
              chartWidth={backChartWidth}
              extendedTimeMax={wallExtendedTimeMax}
              bottomOfGrid={bottomOfGrid}
              topOfGrid={topOfGrid}
              wallZ={wallZ}
              showGrid={showGrid}
              gridColor={gridColor}
              gridOpacity={gridOpacity}
              gridThickness={gridThickness}
              targetTicksX={10}
              targetTicksY={10}
              showPeriodSeparators={showPeriodSeparators}
              periodColor={periodColor}
              periodOpacity={periodOpacity}
              periodLineStyle={periodLineStyle as string}
              periodLineWidth={periodLineWidth}
              showPeriodLabels={showPeriodLabels}
            />

            {/* Unified 2D Canvas Texture Grid for Floor */}
            <FloorGridTexture 
              data={wallDataVisible}
              drawForceLines={false}
              timeMin={wallTimeMin}
              extendedTimeMax={wallExtendedTimeMax}
              startX={startX}
              width={width}
              chartWidth={backChartWidth}
              zLimit={zLimit}
              zScale={wallZScale}
              floorY={floorY}
              showGrid={showGrid}
              gridColor={gridColor}
              gridOpacity={gridOpacity}
              gridThickness={gridThickness}
              targetTicksX={10}
              targetTicksZ={10}
              timeframe={timeframe}
              showPeriodSeparators={showPeriodSeparators}
              periodColor={periodColor}
              periodOpacity={periodOpacity}
              periodLineStyle={periodLineStyle as string}
              periodLineWidth={periodLineWidth}
              showPeriodLabels={showPeriodLabels}
              colorUp={colorUpStyle}
              colorDown={colorDownStyle}
            />

            {/* Bounding Box Contour for Floor */}
            <FastLine 
              points={[
                startX, floorY + 1, -zLimit,
                startX + width, floorY + 1, -zLimit,
                startX + width, floorY + 1, zLimit,
                startX, floorY + 1, zLimit,
                startX, floorY + 1, -zLimit
              ]} 
              color={gridColor} 
              lineWidth={1} 
              opacity={gridOpacity !== undefined ? Math.max(gridOpacity, 0.2) : 0.4} 
              transparent={true} 
              depthWrite={false} 
            />
            <ProgressiveContour 
              points={[
                startX, floorY + 1, -zLimit,
                startX + width, floorY + 1, -zLimit,
                startX + width, floorY + 1, zLimit,
                startX, floorY + 1, zLimit,
                startX, floorY + 1, -zLimit
              ]} 
              isActive={activePressPlane === 'floor' || isFloorChartOpen} 
              isCompleted={isFloorChartOpen} 
              onComplete={() => {
                setTimeout(() => {
                  setActivePressPlane(null);
                  const currentGesture = useFlowaesStore.getState()?.activeGesture;
                  if (!currentGesture || currentGesture === 'long_press_floor') {
                    useFlowaesStore.getState()?.setActiveGesture?.('long_press_floor');
                    if (onOpenFloorChart) onOpenFloorChart();
                  }
                }, 150);
              }}
            />

            {/* Bounding Box Contour for Side Wall */}
            <FastLine 
              points={[
                sideX + 1, bottomOfGrid, -zLimit,
                sideX + 1, bottomOfGrid, zLimit,
                sideX + 1, topOfGrid, zLimit,
                sideX + 1, topOfGrid, -zLimit,
                sideX + 1, bottomOfGrid, -zLimit
              ]} 
              color={gridColor} 
              lineWidth={1} 
              opacity={gridOpacity !== undefined ? Math.max(gridOpacity, 0.2) : 0.4} 
              transparent={true} 
              depthWrite={false} 
            />
            <ProgressiveContour 
              points={[
                sideX + 1, bottomOfGrid, -zLimit,
                sideX + 1, bottomOfGrid, zLimit,
                sideX + 1, topOfGrid, zLimit,
                sideX + 1, topOfGrid, -zLimit,
                sideX + 1, bottomOfGrid, -zLimit
              ]} 
              isActive={activePressPlane === 'side' || isSideChartOpen} 
              isCompleted={isSideChartOpen} 
              onComplete={() => {
                setTimeout(() => {
                  setActivePressPlane(null);
                  const currentGesture = useFlowaesStore.getState()?.activeGesture;
                  if (!currentGesture || currentGesture === 'long_press_side') {
                    useFlowaesStore.getState()?.setActiveGesture?.('long_press_side');
                    if (onOpenSideChart) onOpenSideChart();
                  }
                }, 150);
              }}
            />

            {/* Unified 2D Canvas Texture Grid for Side Wall */}
            <SideWallGridTexture
              drawPriceLines={false}
              drawForceLines={false}
              minP={wallMinP}
              maxP={wallMaxP}
              getY={wallGetY}
              bottomOfGrid={bottomOfGrid}
              topOfGrid={topOfGrid}
              zLimit={zLimit}
              zScale={wallZScale}
              getForceZ={getWallForceZ}
              sideX={sideX}
              showGrid={showGrid}
              gridColor={gridColor}
              gridOpacity={gridOpacity}
              gridThickness={gridThickness}
              targetTicksY={10}
              targetTicksZ={10}
              colorUp={colorUpStyle}
              colorDown={colorDownStyle}
            />

            {/* Connectors to Walls */}
            <group ref={addToWallShiftGroup}>
              {showConnectors && connectorsPos && (
                 <FastSegments 
                    ref={connectorsLineRef}
                    points={connectorsPos} 
                    vertexColors={connectorsCol} 
                    lineWidth={connectorsThickness} 
                    opacity={connectorsOpacity} 
                    transparent 
                    depthWrite={false} 
                    depthTest={true} 
                    frustumCulled={false} 
                 />
              )}
            </group>

                  {/* Left pointing arrow/indicator aligning with the axis */}

            {/* Price Labels and Ticks on the Left-Side Axis (Back Wall) */}
            <LiveScaleTicks
              domain={livePriceDomain}
              kind="price"
              axis="y"
              targetTicks={10}
              bounds={[bottomOfGrid, topOfGrid]}
              padding={8}
              showLines={showGrid}
              lineA={[startX, 0, wallZ + 0.3]}
              lineB={[startX + width, 0, wallZ + 0.3]}
              lineColor={gridColor}
              lineOpacity={gridOpacity}
              labelGroupPosition={[axisX - 15, 0, wallZ + 1]}
              anchorX="right"
              anchorY="middle"
            />
            {/*
            {priceLabels.map((tick, idx) => (
              <PriceTick 
                key={`price_tick_${idx}`}
                price={tick.price}
                level={tick.level}
                axisX={axisX}
                getY={getY}
                wallZ={wallZ}
                isLeft={false}
                baseSize={priceLabelsSize}
                baseOpacity={priceLabelsOpacity}
              />
            ))}
            */}

            
          </>


        {/* Logos in 3D Space (managed via God Panel) */}
        <OverlayLogos3D sideX={sideX} topY={topY} wallZ={wallZ} startX={startX} width={width} />

        {/* Layout Labels (Fixed in 3D) */}

        {/* Structural Edge Outlines */}
        <GroupedLineSegments 
          lines={[
            { start: [sideX, floorY, wallZ], end: [sideX, topY, wallZ] },
            { start: [sideX, floorY, wallZ], end: [sideX, floorY, frontZ] },
            { start: [sideX, floorY, wallZ], end: [axisX, floorY, wallZ] }
          ]} 
          color={edgesColor} 
          opacity={edgesOpacity} 
        />

        {/* Timeline Navigation Scrubber (Removed) */}

        {showShadowFloor && (
          <>
            {/* Momentum vs Time Projection (Floor) */}
            <group ref={addToWallShiftGroup} position={[0, floorY, 0]} scale={[1, 0, 1]}>
              <group ref={addToWallZGroup}>
              <FastLine ref={floorLineRef} points={wallSmoothedPoints.length > 0 ? wallSmoothedPoints : wallData} vertexColors={wallSmoothedPtsCount > 0 ? wallSmoothedColorsFloor : fullColors3DWall} lineWidth={floorChartLineWidth} opacity={floorChartOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={floorClippingPlanes} />
              </group>
            </group>
            {/* SMAs usam o lote principal (não adiado): deslocamento do lote principal */}
            <group ref={addToShiftGroup} position={[0, floorY, 0]} scale={[1, 0, 1]}>
              <group ref={addToMainZGroup}>
              {showSMA10 && sma10Pts.length > 1 && (
                <FastLine ref={floorSma10Ref} points={sma10Pts} color="#facc15" lineWidth={floorSmaLineWidth} opacity={floorSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={floorClippingPlanes} />
              )}
              {showSMA20 && sma20Pts.length > 1 && (
                <FastLine ref={floorSma20Ref} points={sma20Pts} color="#38bdf8" lineWidth={floorSmaLineWidth} opacity={floorSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={floorClippingPlanes} />
              )}
              {showSMA50 && sma50Pts.length > 1 && (
                <FastLine ref={floorSma50Ref} points={sma50Pts} color="#a855f7" lineWidth={floorSmaLineWidth} opacity={floorSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={floorClippingPlanes} />
              )}
              </group>
            </group>
            
            
            {/* Divider Floor */}
            <FastLine points={[startX, floorY, 0, startX + width, floorY, 0]} color="#666666" lineWidth={2} opacity={0.3} transparent depthWrite={false} />
            
            {/* Bounding Box Contour for Back Wall */}
            <FastLine 
              points={[
                startX, bottomOfGrid, wallZ + 1,
                startX + width, bottomOfGrid, wallZ + 1,
                startX + width, topOfGrid, wallZ + 1,
                startX, topOfGrid, wallZ + 1,
                startX, bottomOfGrid, wallZ + 1
              ]} 
              color={gridColor} 
              lineWidth={1} 
              opacity={gridOpacity !== undefined ? Math.max(gridOpacity, 0.2) : 0.4} 
              transparent={true} 
              depthWrite={false} 
            />
            <ProgressiveContour 
              points={[
                startX, bottomOfGrid, wallZ + 1,
                startX + width, bottomOfGrid, wallZ + 1,
                startX + width, topOfGrid, wallZ + 1,
                startX, topOfGrid, wallZ + 1,
                startX, bottomOfGrid, wallZ + 1
              ]} 
              isActive={activePressPlane === 'back' || isBackWallChartOpen} 
              isCompleted={isBackWallChartOpen} 
              onComplete={() => {
                setTimeout(() => {
                  setActivePressPlane(null);
                  const currentGesture = useFlowaesStore.getState()?.activeGesture;
                  if (!currentGesture || currentGesture === 'long_press_back') {
                    useFlowaesStore.getState()?.setActiveGesture?.('long_press_back');
                    if (onOpenBackWallChart) onOpenBackWallChart();
                  }
                }, 150);
              }}
            />

            {/* Time Ticks (Background Candle Graph) */}
            <DynamicTimeAxis 
              position={[0, bottomOfGrid + 15, wallZ + 1]} 
              rotation={[0, 0, 0]} 
              min={wallTimeMin} 
              max={wallExtendedTimeMax} 
              mapValue={wallGetTimeX} 
              bounds={[startX, startX + width]}
              padding={35}
              anchorX="center"
              anchorY="bottom"
              targetTicks={10}
            />

            {/* Time Ticks (Fixed in 3D on Floor) - Intelligent placement around the floor graph */}
            <DynamicTimeAxis 
              position={[0, floorY + 1, frontZ - 15]} 
              rotation={[-Math.PI / 2, 0, 0]} 
              min={wallTimeMin} 
              max={wallExtendedTimeMax} 
              mapValue={wallGetTimeX} 
              bounds={[startX, startX + width]}
              padding={35}
              anchorX="center"
              anchorY="bottom"
              targetTicks={10}
            />
            {/*
            {data.length > 0 && gridLinesX.map((lx, i) => {
                const t = (lx - startX) / width;
                const point = data.reduce((prev, curr) => Math.abs((data.indexOf(curr) / Math.max(1, data.length - 1)) - t) < Math.abs((data.indexOf(prev) / Math.max(1, data.length - 1)) - t) ? curr : prev);
                if (!point || !point.timestamp) return null;
                const xPos = lx;
                
                const date = new Date(point.timestamp);
                const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
                const dateStr = formatDateShortMonth(date, 'America/Sao_Paulo');
                
                // Position labels gracefully in front of the floor grid
                const labelZ = frontZ + 15;

                let level = 0;
                if (i % 4 === 0) level = 0;
                else if (i % 2 === 0) level = 1;
                else level = 2;

                return (
                  <TimeTick
                    key={`time_tick_group_${i}`}
                    xPos={xPos}
                    timeStr={timeStr}
                    dateStr={dateStr}
                    heightScale={heightScale}
                    wallZ={wallZ}
                    floorY={floorY}
                    labelZ={labelZ}
                    baseSize={timeLabelsSize}
                    baseOpacity={timeLabelsOpacity}
                    level={level}
                  />
                );
            })}
            */}

            {/* Price Labels on Side Wall (Inside back edge / right side) */}
            <LiveScaleTicks
              domain={livePriceDomain}
              kind="price"
              axis="y"
              targetTicks={10}
              bounds={[bottomOfGrid, topOfGrid]}
              padding={8}
              showLines={showGrid}
              lineA={[sideX + 0.3, 0, -zLimit]}
              lineB={[sideX + 0.3, 0, zLimit]}
              lineColor={gridColor}
              lineOpacity={gridOpacity}
              labelGroupPosition={[sideX + 1, 0, -zLimit + 15]}
              labelGroupRotation={[0, Math.PI / 2, 0]}
              anchorX="right"
              anchorY="middle"
            />
            {/*
            {priceLabels.map((tick, idx) => (
              <PriceTick 
                key={`price_tick_side_${idx}`}
                price={tick.price}
                level={tick.level}
                axisX={sideX}
                getY={getY}
                zPos={zLimit}
                isLeft={true}
                textOffset={15}
                rotation={[0, Math.PI / 2, 0]}
                baseSize={priceLabelsSize}
                baseOpacity={priceLabelsOpacity}
              />
            ))}
            */}

            {/* Momentum Ticks on Side Wall */}
            <LiveScaleTicks
              domain={liveForceDomain}
              kind="force"
              axis="z"
              targetTicks={10}
              bounds={[-zLimit, zLimit]}
              padding={5}
              showLines={showGrid}
              lineA={[sideX + 0.3, bottomOfGrid, 0]}
              lineB={[sideX + 0.3, topOfGrid, 0]}
              lineColor={gridColor}
              lineOpacity={gridOpacity}
              labelGroupPosition={[sideX + 1, bottomOfGrid + 15, 0]}
              labelGroupRotation={[0, Math.PI / 2, 0]}
              labelAxis="x"
              labelSign={-1}
              anchorX="center"
              anchorY="bottom"
              secondary={false}
              colorUp={colorUpStyle}
              colorDown={colorDownStyle}
            />
            {/* Momentum Ticks on Floor (Right edge) */}
            <LiveScaleTicks
              domain={liveForceDomain}
              kind="force"
              axis="z"
              targetTicks={10}
              bounds={[-zLimit, zLimit]}
              padding={35}
              showLines={showGrid}
              lineA={[startX, floorY + 0.3, 0]}
              lineB={[startX + width, floorY + 0.3, 0]}
              lineColor={gridColor}
              lineOpacity={gridOpacity}
              labelGroupPosition={[startX + width - 15, floorY + 1, 0]}
              labelGroupRotation={[-Math.PI / 2, 0, 0]}
              labelAxis="y"
              labelSign={-1}
              anchorX="right"
              anchorY="middle"
              secondary={false}
              colorUp={colorUpStyle}
              colorDown={colorDownStyle}
            />
            {/* Real-time Trackers for Current Values */}
            <CurrentValueTrackers
              data={wallDataVisible}
              getY={getYLive}
              getTimeX={wallGetTimeX}
              getForceZ={getForceZLive}
              wallZ={wallZ}
              frontZ={zLimit}
              floorY={floorY}
              axisX={axisX}
              sideX={sideX}
              colorUp={colorUp}
              colorDown={colorDown}
              bottomOfGrid={bottomOfGrid}
              backChartWidth={backChartWidth}
              startX={startX}
              finalLivePos={finalLivePos}
              backClippingPlanes={backClippingPlanes}
              floorClippingPlanes={floorClippingPlanes}
              sideClippingPlanes={sideClippingPlanes}
            />
            {/*
            {gridLinesZ.map((lz, idx) => {
                const momVal = lz / zScale;
                const isZero = Math.abs(lz) < 0.001;
                
                let level = 0;
                if (idx % 2 === 0) level = 0;
                else level = 1;

                return (
                  <MomentumTick
                    key={`mom_tick_${idx}`}
                    lz={lz}
                    momVal={momVal}
                    isZero={isZero}
                    sideX={sideX}
                    heightScale={heightScale}
                    startX={startX}
                    width={width}
                    floorY={floorY}
                    baseSize={momentumLabelsSize}
                    baseOpacity={momentumLabelsOpacity}
                    level={level}
                  />
                );
            })}
            */}

            {/* Floor Grid/Labels are handled by Html components above */}
          </>
        )}

        {showShadowSide && (
          <>
            {/* Price vs Momentum Projection (Side wall - Phase Space) */}
            {/* Stays on STARTX = LEFT side */}
            <group position={[sideX, 0, 0]} scale={[0, 1, 1]}>
              <group ref={addToWallYZGroup}>
              <FastLine ref={sideWallLineRef} points={wallSmoothedPoints.length > 0 ? wallSmoothedPoints : wallData} vertexColors={wallSmoothedPtsCount > 0 ? wallSmoothedColorsSide : fullColors3DWall} lineWidth={sideChartLineWidth} opacity={sideChartOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={sideClippingPlanes} />
              </group>
              <group ref={addToMainYZGroup}>
              {showSMA10 && sma10Pts.length > 1 && (
                <FastLine ref={sideSma10Ref} points={sma10Pts} vertexColors={sma10Cols} lineWidth={sideSmaLineWidth} opacity={sideSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={sideClippingPlanes} />
              )}
              {showSMA20 && sma20Pts.length > 1 && (
                <FastLine ref={sideSma20Ref} points={sma20Pts} vertexColors={sma20Cols} lineWidth={sideSmaLineWidth} opacity={sideSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={sideClippingPlanes} />
              )}
              {showSMA50 && sma50Pts.length > 1 && (
                <FastLine ref={sideSma50Ref} points={sma50Pts} vertexColors={sma50Cols} lineWidth={sideSmaLineWidth} opacity={sideSmaOpacity} transparent depthWrite={false} depthTest={true} frustumCulled={false} clippingPlanes={sideClippingPlanes} />
              )}
              </group>
            </group>
            
            
            {/* Divider Side */}
            <FastLine points={[sideX, heightScale/2, 0, sideX, -heightScale/2, 0]} color="#666666" lineWidth={2} opacity={0.3} transparent depthWrite={false} />
            
            {/* Connection labels already handled above */}
          </>
        )}

        {(showShadowFloor || showShadowSide) && (
          <CubeNames
            startX={startX} width={width} floorY={floorY} zLimit={zLimit} sideX={sideX}
            topOfGrid={topOfGrid} bottomOfGrid={bottomOfGrid} axisX={axisX} wallZ={wallZ} heightScale={heightScale}
            buyersColor={buyersColor} sellersColor={sellersColor}
            showFloor={showShadowFloor} showSide={showShadowSide}
          />
        )}

        {showMidLineWall && (
          <group>
            {/* The 50% Depth Wall (Momentum = 0) */}
            <mesh 
              renderOrder={1}
              position={[startX + width / 2, (floorY + topY) / 2, 0]}
              raycast={interactionMode === 'time' ? () => {} : THREE.Mesh.prototype.raycast}
              onPointerDown={(e) => {
                if (interactionMode !== 'orbit') e.stopPropagation();
                if (interactionMode === 'time') return;
                longPressMidWallStartRef.current = { x: e.clientX, y: e.clientY };
                // Start timer for long press (1.5 seconds)
                if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = setTimeout(() => {
                  setShowMidWallOptions(true);
                  if (onOpenMidWallChart) onOpenMidWallChart();
                  longPressTimerRef.current = null;
                }, 1500);
              }}
              onPointerMove={(e) => {
                if (interactionMode !== 'orbit') e.stopPropagation();
                const dist = Math.hypot(e.clientX - longPressMidWallStartRef.current.x, e.clientY - longPressMidWallStartRef.current.y);
                if (dist > 30 && longPressTimerRef.current) {
                  clearTimeout(longPressTimerRef.current);
                  longPressTimerRef.current = null;
                }
              }}
              onPointerUp={(e) => {
                if (interactionMode !== 'orbit') e.stopPropagation();
                if (longPressTimerRef.current) {
                  clearTimeout(longPressTimerRef.current);
                  longPressTimerRef.current = null;
                }
              }}
              onPointerLeave={() => {
                if (longPressTimerRef.current) {
                  clearTimeout(longPressTimerRef.current);
                  longPressTimerRef.current = null;
                }
              }}
            >
              <planeGeometry args={[width, topOfGrid - bottomOfGrid]} />
              <meshBasicMaterial toneMapped={false} 
                color={midLineWallColor} 
                transparent 
                depthWrite={false} 
                opacity={midLineWallOpacity}  
                side={THREE.DoubleSide} 
              />
            </mesh>
            
            <group visible={showMidWallOptions}>
              <Html position={[startX + width / 2, (floorY + topY) / 2, 0]}  style={{ display: showMidWallOptions ? 'block' : 'none', pointerEvents: showMidWallOptions ? 'auto' : 'none' }}>
                <div className="bg-[#0a0e27]/95 backdrop-blur-xl border border-[#00d9ff]/30 rounded-xl p-2 flex flex-col gap-2 pointer-events-auto shadow-2xl">
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setShowMidLineWall(false);
                        setShowMidWallOptions(false);
                      }}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all flex items-center justify-center gap-2"
                      title="Ocultar Parede"
                    >
                      <Trash2 size={14} /> <span className="text-[10px] uppercase font-bold">Ocultar Parede</span>
                    </button>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setShowMidWallOptions(false);
                      }}
                      className="p-2 rounded-lg hover:bg-[#00d9ff]/20 text-[#00d9ff] transition-all flex items-center justify-center"
                      title="Fechar Opções"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </Html>
            </group>

            {/* Top outline to make the edge visible */}
            <FastLine points={[startX, topY, 0, startX + width, topY, 0]} 
              color={midLineWallColor} 
              lineWidth={1} 
              opacity={Math.min(1, midLineWallOpacity * 5)} 
              transparent 
              renderOrder={1}
            depthWrite={false} />
            {/* Bottom outline */}
            <FastLine points={[startX, floorY, 0, startX + width, floorY, 0]} 
              color={midLineWallColor} 
              lineWidth={1} 
              opacity={Math.min(1, midLineWallOpacity * 5)} 
              transparent 
              renderOrder={1}
            depthWrite={false} />
            {/* Center line (optional, subtle dash) */}
            <FastLine points={[startX, (floorY + topY) / 2, 0, startX + width, (floorY + topY) / 2, 0]} 
              color={midLineWallColor} 
              lineWidth={1} 
              opacity={Math.min(1, midLineWallOpacity * 3)} 
              dashed dashSize={5} gapSize={5}
              transparent 
              renderOrder={1}
            depthWrite={false} />
          </group>
        )}
      </group>
      )}
    </>
  );
};


const MemoizedSceneContent = React.memo(SceneContent, (prev, next) => {
  // If data array is the same reference, don't rerender
  if (prev.data !== next.data) return false;
  if (prev.interactionMode !== next.interactionMode) return false;
  if (prev.timeframe !== next.timeframe) return false;
  // Ignore targetHistoryOffset and crosshair mutations that aren't data changes
  return true; 
});
export default MemoizedSceneContent;
